import type {
  Anmerkung,
  Auftrag,
  BildEntscheidung,
  EntscheidungsPayload,
  EntwurfSegmentEintrag,
  PdpDetails,
  RatgeberStruktur,
  TextAnker,
  TextAntwort,
} from '@awf/shared';
import {
  MAX_ANMERKUNGEN,
  MAX_KOMMENTAR_ZEICHEN,
  MAX_KORREKTURRUNDEN,
  MAX_ZITAT_ZEICHEN,
  offeneRunde,
} from '@awf/shared';
import { type FormEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { bildlaufVerhalten } from '../ui/bewegung.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { HinweisBereich, type HinweisDaten } from '../ui/hinweis.js';
import { KiHinweis } from '../ui/ki-hinweis.js';
import { Seitenkopf } from '../ui/seitenkopf.js';
import { EmptyState, ErrorState, LoadingState } from '../ui/states.js';
import { Statusmarke, TON_JE_GRUPPE } from '../ui/statusmarke.js';
import {
  abschnittsTexte,
  entfernenBeschriftung,
  entferneBloecke,
  ersetzeBlock,
  geaenderteSegmente,
  segmentGrenzen,
  textAenderungen,
  textAenderungenText,
} from './abschnitte.js';
import {
  kannHinzufuegen,
  kurzesZitat,
  neueAnmerkung,
  ungueltigeAnmerkungen,
  type AnmerkungsFehler,
} from './anmerkungen.js';
import { AuswahlLeiste } from './auswahl-leiste.js';
import { BildBewertung, bildName, istHeaderBild, type TextBild } from './bild-bewertung.js';
import { BildLupeProvider } from './bild-lupe.js';
import { zerlegeInBloecke } from './bloecke.js';
import { leiteEntscheidungAb } from './entscheidung.js';
import stile from './freigabe-page.module.css';
import {
  schneide,
  schneideAnUrsprung,
  schneideNachSegmentierung,
  zusammen,
  type Segment,
  type SchnittAnUrsprung,
} from './markdown-segmente.js';
import { holeRegistrierung, zeichneBereiche } from './hervorhebung.js';
import { ankerImText, bereichImAbschnitt, wiederfinden } from './markierung.js';
import { zaehlePruefhinweise } from './marker.js';
import {
  type OriginalQuelle,
  originalQuelle,
  OriginaltextAufklapper,
} from './originaltext-aufklapper.js';
import { PruefhinweisDialog } from './pruefhinweis-dialog.js';
import { VerwerfenDialog } from './verwerfen-dialog.js';
import { RueckmeldeSpalte } from './rueckmelde-spalte.js';
import type { AuftragsQuelle } from './runs-source.js';
import { gruppeVon, STATUS_TEXT } from './status.js';
import { type Auswahl, auswahlStehtNoch, positionVon, TextFlaeche } from './text-flaeche.js';
import { AnhangBereich } from './anhang-bereich.js';
import { KontextKasten } from './kontext-kasten.js';
import { giltFuer, type Stempel } from './text-stand.js';
import {
  type EntwurfSchreibergebnis,
  type LoescheEntwurfFn,
  type SpeichereEntwurfFn,
  useEntwurfSchreiber,
} from './use-entwurf.js';
import { rundeBeansprucht, schichtArtAusFirestore, type Absendung } from './uebergabe.js';
import { UebergabeSchicht } from './uebergabe-schicht.js';
import { useAuftrag } from './use-runs.js';

export interface FreigabeSeiteProps {
  quelle: AuftragsQuelle;
  holen: (runId: string) => Promise<TextAntwort>;
  senden: (runId: string, payload: EntscheidungsPayload) => Promise<void>;
  /**
   * Speichert den Zwischenstand. `runde` reist mit (Ruling 25): Der Server weist damit
   * einen Autosave ab, der erst nach der Entscheidung dieser Runde eintrifft.
   * `segmentierung` (Ruling 34): die Segment-Zuordnung dieses Entwurfs zum Zeitpunkt DIESES
   * Speicherns, gelesen statt erraten beim naechsten Laden -- siehe `segmentierungFuer`
   * unten.
   * `anmerkungen`: die Liste dieser Runde, immer als Liste, auch leer (AGENTS.md Regel 5).
   */
  speichereEntwurf: SpeichereEntwurfFn;
  /**
   * Verwirft einen Entwurf serverseitig (Knopf "Fassung aus n8n laden", Ruling 20).
   * `runde` reist mit (Codex-Gate-Befund HOCH, Fix-Runde 6, Spiegelbild von Ruling 25):
   * der Server weist ein verzoegertes Loeschen ab, das nicht mehr zur aktuellen Runde
   * gehoert.
   */
  loescheEntwurf: LoescheEntwurfFn;
  /**
   * Holt die Befund-Details mit dem Originaltext der bestehenden Seite (Rueckmeldung Theresa
   * 2026-11-02). Fehlt sie, zeigt die Seite keinen Originaltext.
   */
  holeOriginal?: (quelle: OriginalQuelle) => Promise<PdpDetails>;
}

const BILD_VALIDIERUNG = 'Bitte beschreiben Sie, was am Bild geändert werden soll.';
/**
 * `apps/backend/src/n8n.ts` (`alsTextAntwort`) meldet einen leeren/nur-Leerraum-Text
 * bereits als `nicht_abrufbar`, bevor er die Zwischenschicht verlaesst -- diese Garantie
 * liegt aber AUSSERHALB der Komponente, die nur die generische `holen`-Schnittstelle
 * kennt. Ohne eigene Pruefung wuerde eine andere Quelle (oder ein kuenftiger
 * Backend-Umbau) einen leeren Entwurf kommentarlos als freigebbar anzeigen -- ein Fehler,
 * der wie ein Ergebnis aussieht (Codex-Gate-Befund HOCH).
 */
const TEXT_LEER_TEXT = 'Der Text ist leer zurückgekommen.';
const TEXT_ENTFERNT_TEXT =
  'Der Text ist leer. Holen Sie ihn über „Rückgängig“ zurück oder wählen Sie oben die „Fassung der Content Engine“.';
// 2s nach der letzten Eingabe, nicht nach dem Oeffnen (Task 13, Brief Step 3): ein Segment,
// das nur geoeffnet und nie angefasst wurde, hat keine "letzte Eingabe", ab der gezaehlt
// werden koennte -- der Timer entsteht deshalb ausschliesslich im Eingabe-Handler, nie in
// einem Effekt, der bereits beim Oeffnen anspringt.
const ENTWURF_VERZOEGERUNG_MS = 2000;
// Aufblinken der Sprungstelle: eigene Highlight-Registrierung (Regel in text-flaeche.module.css)
// und die Zeit, nach der sie wieder verschwindet. Gezaehlt wird ab dem letzten Scroll-Ereignis:
// Der sanfte Bildlauf dauert je nach Strecke bis ~1,5 s, ab dem Klick gezaehlt war das Aufblinken
// vorbei, sobald die Stelle ankam (Browsertest B19). Die Obergrenze gilt, falls die Nutzerin
// danach selbst weiterscrollt.
const SPRUNGZIEL = 'awf-sprungziel';
const SPRUNGZIEL_MS = 1500;
const SPRUNGZIEL_HOECHSTENS_MS = 4000;
const ZEITPUNKT_FORMAT = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const ZWISCHENSTAND_OHNE_ZEIT = 'Ihre Bearbeitung (aktuell)';
const ANMERKUNG_UNGUELTIG_TEXT =
  'Eine Anmerkung zeigt auf keine Textstelle mehr. Bitte löschen oder neu setzen.';
// I-2: Die offene Markierung hat ihren eigenen Gegenstand, das Feld -- anders als eine Anmerkung
// der Liste, die dort gekennzeichnet wird.
const MARKIERUNG_VERALTET_TEXT =
  'Die markierte Stelle hat sich geändert. Bitte markieren Sie sie neu.';

/** Das Kommentarfeld: sein Anker samt Textstand und die Stelle der Leiste (`text-stand.ts`). */
interface OffeneMarkierung extends Stempel {
  anker: TextAnker;
  position: Auswahl['position'];
}

/** Steht der Anker noch an seinen Offsets in diesem Text? Dieselbe Pruefung wie fuer die Liste. */
function ankerGilt(anker: TextAnker, texte: ReadonlyMap<string, string>): boolean {
  return ungueltigeAnmerkungen([neueAnmerkung(anker, '')], texte).length === 0;
}

/** Dieselbe Stelle, gleich wo der Kontext davor und danach inzwischen steht. */
function gleicheStelle(a: TextAnker, b: TextAnker): boolean {
  return (
    a.abschnittId === b.abschnittId && a.von === b.von && a.bis === b.bis && a.zitat === b.zitat
  );
}

/**
 * Ruling 34: liest die Segment-Zuordnung eines geladenen Entwurfs aus `segmentierung`,
 * statt sie ueber `schneideAnUrsprung` aus dem Text zu ERRATEN. Modul-Ebene, keine
 * Komponenten-Abhaengigkeit: reine Funktion von `entwurf`/`struktur` allein.
 *
 * Liefert `null` -- und damit den Wunsch an die Aufruferin, auf `schneideAnUrsprung`
 * zurueckzufallen --, wenn:
 * - der Entwurf GAR KEINE `segmentierung` traegt (Altbestand von VOR diesem Ruling), ODER
 * - `struktur` fuer diesen Lauf fehlt (ohne sie lassen sich `abschnittIds` nicht auf
 *   `urSegmente`-Indizes zurueckuebersetzen), ODER
 * - die gespeicherten Laengen nicht mehr zu `entwurf.text` passen (`schneideNachSegmentierung`
 *   liefert dann selbst schon `null`, siehe dort), ODER
 * - eine `abschnittId` in der Segmentierung in der AKTUELLEN `struktur` nicht mehr vorkommt
 *   (z. B. weil n8n seit dem Speichern eine neue Struktur mit neuen IDs geliefert hat) --
 *   in jedem dieser Faelle ist die gespeicherte Zuordnung nicht mehr vertrauenswuerdig, und
 *   die alte Ankersuche bleibt die einzig sinnvolle Rueckfallebene.
 */
function segmentierungEinlesen(
  segmentierung: EntwurfSegmentEintrag[] | undefined,
  entwurfText: string,
  struktur: RatgeberStruktur | undefined,
): SchnittAnUrsprung | null {
  if (!segmentierung || !struktur) return null;
  const segmente = schneideNachSegmentierung(entwurfText, segmentierung);
  if (!segmente) return null;
  const indexVonId = new Map(struktur.abschnitte.map((a, i) => [a.abschnittId, i]));
  const herkunft: number[][] = [];
  for (const eintrag of segmentierung) {
    const indizes: number[] = [];
    for (const id of eintrag.abschnittIds) {
      const idx = indexVonId.get(id);
      if (idx === undefined) return null;
      indizes.push(idx);
    }
    herkunft.push(indizes);
  }
  return { segmente, herkunft };
}

/**
 * Task-13-Re-Review NEU-12: `Intl.DateTimeFormat.format` wirft bei einem ungueltigen Datum
 * (`RangeError: Invalid time value`). Ungeschuetzt im Rendern riss ein unplausibles
 * `gespeichertAm` -- Altbestand oder ein manueller Firestore-Eingriff -- die GANZE
 * Freigabeseite ab, denn es gibt keine Error-Boundary. Der Zwischenstand selbst bleibt
 * gueltig und verwerfbar; nur der Zeitpunkt entfaellt dann.
 */
function zwischenstandText(gespeichertAm: string): string {
  const zeitpunkt = new Date(gespeichertAm);
  if (Number.isNaN(zeitpunkt.getTime())) return ZWISCHENSTAND_OHNE_ZEIT;
  return `Ihre Bearbeitung vom ${ZEITPUNKT_FORMAT.format(zeitpunkt)} Uhr (aktuell)`;
}

/**
 * Kopf der Freigabeseite im Muster aller Seiten (`Seitenkopf`): Weg zurueck, Thema als Titel und
 * ein Satz, was hier zu tun ist. Stufe c (Spec 2026-10-12 Abschnitt 5.5) bleibt: der erste
 * Wegpunkt fuehrt in die Review-Liste DIESES Typs. Keine Lauf-Kennung mehr im Kopf (Rueckmeldung
 * 2026-10-13), die Runde steht im Satz.
 */
/**
 * Die Fassungsauswahl wie `DraftHistory` der Content-Factory ("Fassung 3 (aktuell)"), statt des
 * frueheren Blocks "Zwischenstand" mit "Fassung aus n8n laden" in der Rueckmeldespalte
 * (Rueckmeldung 2026-10-13). Fruehere Runden speichern wir nicht; zur Wahl stehen die eigene
 * Bearbeitung und die Fassung der Content Engine. Die Wahl der Content-Engine-Fassung verwirft
 * die eigene Bearbeitung.
 */
export interface FassungsWahl {
  bearbeitung: string;
  deaktiviert: boolean;
  onContentEngine(): void;
}

function SeitenKopf({
  auftrag,
  runId,
  fassung,
}: {
  auftrag: Auftrag;
  runId: string;
  fassung?: FassungsWahl | undefined;
}) {
  return (
    <Seitenkopf
      titel={auftrag.thema}
      pfad={[
        { text: 'Review', ziel: `/?typ=${auftrag.typ}&gruppe=wartet` },
        { text: 'Fortschritt Ihres Auftrags', ziel: `/auftraege/${encodeURIComponent(runId)}` },
      ]}
      beschreibung={
        <span className={stile.rundenZeile}>
          Freigaberunde {auftrag.runde ?? 1}
          {fassung && (
            <select
              className={stile.fassung}
              aria-label="Textfassung"
              value="bearbeitung"
              disabled={fassung.deaktiviert}
              onChange={(e) => {
                if (e.target.value === 'content-engine') fassung.onContentEngine();
              }}
            >
              <option value="bearbeitung">{fassung.bearbeitung}</option>
              <option value="content-engine">Fassung der Content Engine</option>
            </select>
          )}
        </span>
      }
      aktion={
        <Statusmarke ton={TON_JE_GRUPPE[gruppeVon(auftrag.status)]}>
          {STATUS_TEXT[auftrag.status]}
        </Statusmarke>
      }
    />
  );
}

/**
 * Freigabeseite: ein Mensch liest den erzeugten Ratgeber-Text, bewertet ihn und die
 * Bilder, gibt frei oder bestellt eine Korrekturrunde.
 *
 * Die Schreibsperre folgt einer Regel (Spec 2026-09-29-absende-rennen Abschnitt 5.1): gesperrt, solange
 * laut Firestore keine Runde offen ist (`offeneRunde`) oder dieser Tab zur offenen Runde schon
 * abgeschickt hat (`absendung`, gesetzt beim Klick). Darueber steht die `UebergabeSchicht`.
 */
export function FreigabePage({
  quelle,
  holen,
  senden,
  speichereEntwurf,
  loescheEntwurf,
  holeOriginal,
}: FreigabeSeiteProps) {
  const { runId = '' } = useParams();
  // Kein erzwungenes Einklappen der Seitenleiste mehr (Rueckmeldung 2026-10-14: "hier ist doch
  // genug Platz"). Es gilt die gespeicherte Wahl wie auf jeder anderen Seite.
  const zustand = useAuftrag(quelle, runId);
  // Die EINE Regel (Spec 2026-09-29-absende-rennen Abschnitt 4.2): Zu welcher Runde nimmt dieser Lauf
  // gerade eine Entscheidung an? `null`, solange die letzte entschieden und die naechste noch nicht
  // vorgelegt ist -- genau dann ist die Seite gesperrt, egal in welcher Reihenfolge 202 und
  // Firestore-Aufnahme ankommen. Solange der Auftrag noch laedt, ist ebenfalls nichts offen: Der
  // Ladeeffekt feuert erst mit der echten Aufnahme (MITTEL-3, Task-12-Review).
  const offen = zustand.art === 'daten' && zustand.auftrag ? offeneRunde(zustand.auftrag) : null;
  // Das eigene Absenden, gesetzt BEIM KLICK (Spec Abschnitt 5.1). Es sperrt nur die Runde, zu der es gehoert.
  const [absendung, setAbsendung] = useState<Absendung | null>(null);
  const absendungRef = useRef<Absendung | null>(null);
  // Der einzige Weg, `absendung` zu setzen: Zustand und Ref aendern sich zusammen.
  function setzeAbsendung(neu: Absendung | null) {
    absendungRef.current = neu;
    setAbsendung(neu);
  }
  const eigeneAbsendung = absendung !== null && absendung.runId === runId ? absendung : null;
  // Das eigene Absenden bekam `runde_veraltet`, waehrend Firestore die Runde noch als offen zeigt:
  // Der Server hat sie schon anderswo entschieden. Bis die Aufnahme das nachzieht, sperrt dieser
  // Merker die Runde -- sonst braechte jeder weitere Klick nur die naechste 409 (Codex-Runde 1
  // zu PR 48, HOCH-1). Der Ladeeffekt leert ihn, sobald `offen` wechselt oder der Lauf wechselt.
  const [veraltet, setVeraltet] = useState<{ runId: string; runde: number } | null>(null);
  const gesperrt =
    offen === null ||
    (eigeneAbsendung !== null && eigeneAbsendung.runde === offen) ||
    (veraltet !== null && veraltet.runId === runId && veraltet.runde === offen);
  // Fuer den `catch` nach `await senden(...)`: Ist die Runde JETZT offen (nicht beim Klick)?
  const offenRef = useRef(offen);
  offenRef.current = offen;
  // Fuer den Vergleich nach `await senden(...)`: Ist das noch der Lauf, zu dem abgeschickt wurde?
  // Bewusst NICHT `laufToken` -- den zaehlt der Ladeeffekt bei jedem Wechsel von `offen` hoch, und
  // genau das verwarf vorher den eigenen Erfolg (Beleg 2026-09-29).
  const aktuelleRunId = useRef(runId);
  aktuelleRunId.current = runId;
  // Ist die Seite noch eingehaengt? Ein spaetes 202 nach dem Verlassen darf nicht per `navigate`
  // zurueckholen (Codex-Runde 1 zu PR 48, HOCH-2). Im Effekt gesetzt statt beim Anlegen, damit
  // der StrictMode-Durchlauf (Aufbau, Abbau, Aufbau) mit `true` endet.
  const aktiv = useRef(false);
  useEffect(() => {
    aktiv.current = true;
    return () => {
      aktiv.current = false;
    };
  }, []);
  // Fuer welchen Lauf der Ladeeffekt zuletzt lief: erkennt dort einen echten Laufwechsel.
  const effektRunId = useRef<string | null>(null);
  const navigate = useNavigate();
  const [textAntwort, setTextAntwort] = useState<TextAntwort | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  // Treibt einen manuellen Wiederholungsversuch nach einem Ladefehler an (NIEDRIG,
  // Task-12-Review): eine eigene Zahl statt eines Bool, damit ein zweiter Klick nach einem
  // erneut fehlgeschlagenen Versuch den Ladeeffekt trotzdem wieder ausloest.
  const [wiederholVersuch, setWiederholVersuch] = useState(0);
  // Die Frage "Trotzdem freigeben?" (Spec 2026-10-12 §5.5): die Zahl offener Pruefhinweise beim
  // Klick, oder null, solange nicht gefragt wird.
  const [pruefhinweisFrage, setPruefhinweisFrage] = useState<number | null>(null);
  // Rueckfrage vor dem Verwerfen der eigenen Bearbeitung (Fassungsauswahl im Kopf). Gebunden an
  // Lauf und Runde (lokaler Codex-Review PR #70): Wechselt eines davon, gilt die Frage nicht mehr
  // und darf nicht spaeter fuer einen anderen Stand wieder auftauchen.
  const [verwerfenFrage, setVerwerfenFrage] = useState<{ runId: string; runde: number } | null>(
    null,
  );
  // Wird die Seite gesperrt, ist die Frage erledigt -- sie darf nicht spaeter von selbst aufgehen.
  useEffect(() => {
    if (gesperrt) setPruefhinweisFrage(null);
  }, [gesperrt]);
  // Der Text samt seinem Textstand (`text-stand.ts`) in EINEM Zustand: Beide aendern sich nur
  // zusammen, und nur ueber `setzeSegmente` unten.
  const [text, setText] = useState<{ segmente: Segment[]; stand: number }>({
    segmente: [],
    stand: 0,
  });
  const segmente = text.segmente;
  const textStand = text.stand;
  // Der zuletzt vergebene Stand. "Rückgängig" prueft Sekunden spaeter gegen ihn, nicht gegen den
  // Stand seines eigenen Renders.
  const standZaehler = useRef(0);
  const [urSegmente, setUrSegmente] = useState<Segment[]>([]);
  // Fuer jedes `segmente[i]` die `urSegmente`-Indizes, die es zusammenfasst (Task-13-
  // Re-Review NEU-1): normalerweise genau einer je Segment (siehe `trivialeHerkunft`
  // unten), mehr als einer nur dort, wo `schneideAnUrsprung` beim Laden eines Entwurfs
  // einen Anker nicht wiedergefunden hat. Treibt in `absenden` die praezise Meldung von
  // `bearbeiteteAbschnitte`, auch wenn `segmente.length` von `urSegmente.length` abweicht.
  const [herkunft, setHerkunft] = useState<number[][]>([]);
  // Genau ein Block der Textflaeche gleichzeitig in Bearbeitung (Index in `zerlegung.bloecke`).
  // `bearbeitungsRaw` ist das zurueckuebersetzte Markdown dieses Blocks, samt dem Endleerraum
  // seines Original-`raw` (Uebergabe B1 -> B2, Abschnitt 2: nicht trimmen).
  const [offenerBlock, setOffenerBlock] = useState<number | null>(null);
  const [bearbeitungsRaw, setBearbeitungsRaw] = useState('');
  // Ref statt State: der Timer selbst ist kein Anzeigezustand, nur seine Existenz muss sich
  // ueber Tastenanschlaege hinweg debouncen lassen, ohne dafuer einen Render auszuloesen.
  const entwurfTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Das kurze Aufblinken der Sprungstelle (`zurStelle`): Timer plus der Rahmen, unter dem die
  // Registrierung 'awf-sprungziel' ihre Range fuehrt. Beim Verlassen der Seite wird beides
  // abgeraeumt, sonst hinge eine Range auf entfernten Knoten in der globalen Registrierung.
  const sprungzielTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sprungzielRahmen = useRef<Element | null>(null);
  const sprungzielLauscher = useRef<(() => void) | null>(null);
  // Wurde WAEHREND der aktuellen Bearbeitungssitzung (seit dem letzten `blockOeffnen`)
  // schon mindestens einmal ein Autosave abgeschickt? `blockAbbrechen` muss diesen dann
  // serverseitig rueckgaengig machen (HOCH-4) -- ein rein lokales Verwerfen liesse den
  // Entwurf in Firestore stehen, obwohl die Bearbeitung ausdruecklich abgebrochen wurde.
  const sitzungGespeichert = useRef(false);
  // Gibt es seit dem letzten tatsaechlichen Schreibversuch (Debounce-Ablauf ODER Punkt-C-
  // Soforthandhabe) eine Aenderung, die noch NICHT auf den Weg gebracht wurde? Getrennt vom
  // blossen "laeuft gerade ein Timer" (`entwurfTimer`): `absenden` bricht dessen Timer bei
  // JEDEM Versuch ab (Ruling 24), auch bei einem gescheiterten -- ohne dieses eigene Merkmal
  // wuesste ein anschliessendes `blur`/`visibilitychange` nicht mehr, dass der getippte Text
  // noch aussteht (Punkt C, die konkrete Luecke, die frueher Ruling 26 einzeln geflickt hat).
  const entwurfUngesichert = useRef(false);
  // Dasselbe fuer die Anmerkungsliste, aber nicht an eine Bearbeitungssitzung gebunden: Oeffnen
  // und Abbrechen eines Segments raeumen den Timer ab, der eine Listenaenderung tragen kann.
  // Ein gescheiterter oder verworfener Schreibauftrag setzt es wieder (siehe `entwurfSchreiben`
  // und `absenden`) -- sonst ginge etwa das Loeschen der letzten Anmerkung still verloren.
  const anmerkungenUngesichert = useRef(false);

  // Invalidiert jeden noch laufenden Abruf und Entwurfs-Schreibauftrag aus einem VERLASSENEN
  // Lauf-/Runden-Stand: React Router tauscht bei einem reinen `:runId`-Wechsel dieselbe
  // Komponenteninstanz weiter (kein Unmount), und ein spaet aufloesender Auftrag fuer den alten
  // Lauf wuerde sonst den laengst angezeigten NEUEN Lauf veraendern (Codex-Gate-Befund HOCH).
  // `senden` vergleicht dagegen nur den Lauf (`aktuelleRunId`, Spec 2026-09-29-absende-rennen Abschnitt 5.1).
  const laufToken = useRef(0);

  const {
    planeEntwurfSchreiben,
    verwirfAusstehenden,
    entwurfHinweis,
    setEntwurfHinweis,
    entwurfFehler,
    setEntwurfFehler,
    entwurfLoeschenFehler,
    setEntwurfLoeschenFehler,
    entwurfWirdVerworfen,
    setEntwurfWirdVerworfen,
  } = useEntwurfSchreiber({
    speichereEntwurf,
    loescheEntwurf,
    laufToken,
    beiFehler: () => {
      anmerkungenUngesichert.current = true;
    },
  });

  const [textFeedback, setTextFeedback] = useState('');
  const [anmerkungen, setAnmerkungen] = useState<Anmerkung[]>([]);
  const [offeneMarkierung, setOffeneMarkierung] = useState<OffeneMarkierung | null>(null);
  const [hinweisText, setHinweisText] = useState('');
  const [anmerkungsFehler, setAnmerkungsFehler] = useState<AnmerkungsFehler | null>(null);
  const [bildEntscheidungen, setBildEntscheidungen] = useState<BildEntscheidung[]>([]);
  const [bildValidierung, setBildValidierung] = useState<string | null>(null);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  // Das eigene Absenden ist gescheitert (nicht `runde_veraltet`), waehrend Firestore den Anspruch
  // noch zeigt: Das Backend gibt die Runde erst nach seiner 502 frei (`gibRundeFrei`), die
  // Freigabe-Aufnahme kommt also meist NACH der Antwort. Bis dahin ist `offen` null und die Seite
  // gesperrt -- ohne diesen Merker stuende darueber "Wird freigegeben ..." statt der Meldung.
  // Geleert beim naechsten Absenden, beim Reset und sobald die Runde wieder offen ist.
  const [fehlgeschlagen, setFehlgeschlagen] = useState<{ runId: string; runde: number } | null>(
    null,
  );
  const [auswahl, setAuswahl] = useState<Auswahl | null>(null);
  const [hervorgehoben, setHervorgehoben] = useState<Anmerkung | null>(null);
  const [hervorhebungsAnlass, setHervorhebungsAnlass] = useState(0);
  const [hinweis, setHinweis] = useState<HinweisDaten | null>(null);
  const hinweisZaehler = useRef(0);
  // M6: der Block, der nach "Rückgängig" den Fokus bekommt (der Knopf verschwindet mit dem
  // Hinweis).
  const fokusNachRueckgaengig = useRef<number | null>(null);
  const flaechenRahmen = useRef<HTMLDivElement>(null);
  // Abbau der Nachkorrektur nach "Zur Textstelle" (Listener und Zeitgrenze). Beim Verlassen der
  // Seite darf davon nichts weiterlaufen.
  const nachzugAbbau = useRef<(() => void) | null>(null);
  useEffect(() => () => nachzugAbbau.current?.(), []);
  // "Rückgängig" laeuft Sekunden spaeter; es braucht dann die AKTUELLE Speicherfunktion
  // (sonst schriebe es eine veraltete Anmerkungsliste mit) und den aktuellen Sperrzustand.
  const planeSpeichernRef = useRef<(ziel: Segment[]) => void>(() => {});
  const gesperrtRef = useRef(false);
  const entfernenRef = useRef<() => void>(() => {});
  // Markieren im offenen Block (Live-Test 2026-11-03): Der Anker der Markierung wartet, bis der
  // Block geschlossen und sein Text uebernommen ist, und wird erst dann im neuen Textstand
  // verankert. Die Ref traegt die Funktion DIESES Renders, wie `entfernenRef`.
  const [nachSchliessenVerankern, setNachSchliessenVerankern] = useState<TextAnker | null>(null);
  const verankernRef = useRef<(anker: TextAnker) => void>(() => {});
  useEffect(() => {
    if (!nachSchliessenVerankern) return;
    setNachSchliessenVerankern(null);
    verankernRef.current(nachSchliessenVerankern);
  }, [nachSchliessenVerankern]);

  /**
   * Der einzige Weg zu neuen Segmenten: vergibt dafuer einen neuen Textstand und liefert ihn.
   * Jeder Zustand mit einem aelteren Stempel gilt ab hier nur noch nach Nachpruefung an seinem
   * Verwendungsort (`text-stand.ts`). Beruehrt nur eine Ref und einen Setter, ist also auch aus
   * dem Ladeeffekt unten aufrufbar.
   */
  function setzeSegmente(neu: Segment[]): number {
    standZaehler.current += 1;
    const stand = standZaehler.current;
    setText({ segmente: neu, stand });
    return stand;
  }

  // Beruehrt nur Setter und eine Ref, ist also auch aus dem Ladeeffekt unten ohne veraltete
  // Werte aufrufbar.
  function anmerkungenZuruecksetzen() {
    setAnmerkungen([]);
    anmerkungenUngesichert.current = false;
    setOffeneMarkierung(null);
    setHinweisText('');
    setAnmerkungsFehler(null);
  }

  const hinweisFeldRef = useRef<HTMLTextAreaElement>(null);
  // Haelt bei jedem Render die AKTUELLE `entwurfSofortSichern`-Funktion (siehe dort) -- die
  // Referenz selbst bleibt ueber die gesamte Lebensdauer der Komponente stabil, damit der
  // `visibilitychange`-Effekt unten sie nur EINMAL abonnieren muss, aber trotzdem immer den
  // neuesten `offenerBlock`/`bearbeitungsRaw` sieht.
  const entwurfSofortSichernRef = useRef<() => void>(() => {});
  /**
   * Zu welchem `runId`/Runden-Schluessel `textAntwort` gehoert -- als STATE, nicht als Ref.
   * `useEffect` laeuft erst NACH dem Rendern und NACH dem Bildaufbau im Browser (anders als
   * `useLayoutEffect`): Nach einem `runId`-/Runden-Wechsel kann React deshalb einen Frame
   * mit den NEUEN Routenparametern, aber noch dem ALTEN `textAntwort` und den alten
   * Entscheidungen malen, bevor der Effekt zuruecksetzt. Ein `useRef`-Zaehler, der erst IM
   * Effekt hochgezaehlt wird (fruehere Fassung), schliesst dieses Fenster NICHT -- genau in
   * diesem Frame stimmen Ref und Zaehler noch ueberein. Ein State-Vergleich direkt beim
   * Rendern dagegen greift schon auf dem ERSTEN Render mit den neuen Parametern: `geladenFuer`
   * traegt noch den ALTEN Schluessel, `runId`/`offen` schon die neuen -- das
   * Formular (und damit "Absenden") wird gar nicht erst gezeigt, bis der neue Text tatsaechlich
   * da ist (Codex-Gate-Befund HOCH, fuenfte Runde).
   *
   * ES GIBT KEINEN TEST, DER DIESE WACHE BEWEIST (Task-12-Review, MITTEL-2): Die Gegenprobe
   * -- `antwortAktuell` durch `true` ersetzt -- laesst alle Tests gruen, null Abdeckung.
   * Grund: `act()` in Testing Library flusht Effekte synchron, BEVOR die Kontrolle an den
   * Test zurueckgeht -- das genaue "Render mit neuen Parametern, aber der Reset-Effekt ist
   * noch nicht gelaufen"-Fenster laesst sich damit in keinem Test herbeifuehren, egal mit
   * welcher Wache davor. Das ist eine Aussage ueber DIESE Testumgebung, kein Nachweis der
   * Richtigkeit -- die Begruendung oben (React-Doku zu `useEffect` vs. `useLayoutEffect`)
   * traegt inhaltlich, bleibt aber unbelegt.
   */
  const [geladenFuer, setGeladenFuer] = useState<{ runId: string; runde: number } | null>(null);
  // Ohne offene Runde (Uebergabe laeuft) gilt der zuletzt geladene Text weiter: Er steht unter der
  // Uebergabe-Schicht, und die Flaeche ist ohnehin gesperrt (Spec 2026-09-29-absende-rennen Abschnitt 5.2).
  const antwortAktuell =
    geladenFuer !== null &&
    geladenFuer.runId === runId &&
    (offen === null || geladenFuer.runde === offen);

  useEffect(() => {
    const laufGewechselt = effektRunId.current !== runId;
    effektRunId.current = runId;
    // Spec 2026-09-29-absende-rennen Abschnitt 5.2: Ohne offene Runde gibt es nichts zu laden. Der zuletzt
    // geladene Text bleibt unter der Uebergabe-Schicht stehen -- kein Reset, kein Abruf. Ausnahme
    // ist ein echter Laufwechsel: Dann gehoeren Text, Eingaben und ein noch geplanter Autosave zum
    // verlassenen Lauf und werden unten verworfen, auch wenn der neue Lauf nichts zu laden hat.
    // Der `runde_veraltet`-Merker sperrt nur, bis Firestore nachzieht: Jede andere offene Runde
    // (auch `null` -- dann sperrt `offen` selbst) oder ein anderer Lauf macht ihn gegenstandslos.
    // Fuer dieselbe Runde bleibt er stehen, sonst waere er wirkungslos.
    setVeraltet((v) =>
      v !== null && (laufGewechselt || v.runId !== runId || v.runde !== offen) ? null : v,
    );
    if (offen === null && !laufGewechselt) return;
    // Die Runde ist wieder offen (etwa nach `gibRundeFrei`) oder der Lauf hat gewechselt: Ein
    // gemerkter Fehlschlag hat keine Sperre mehr zu erklaeren.
    setFehlgeschlagen(null);
    // Schon fuer genau diese Runde geladen (offen N -> null -> N, wenn n8n nach dem Anspruch
    // scheitert und `gibRundeFrei` die Runde zurueckgibt): Neu laden wuerde die Eingaben der
    // Nutzerin verwerfen, die nach einem gescheiterten Absenden erhalten bleiben muessen (Ruling 23).
    if (
      offen !== null &&
      geladenFuer !== null &&
      geladenFuer.runId === runId &&
      geladenFuer.runde === offen
    ) {
      return;
    }
    laufToken.current += 1;
    const meinToken = laufToken.current;
    setzeAbsendung(null);

    setTextAntwort(null);
    setGeladenFuer(null);
    setLadeFehler(null);
    setOffenerBlock(null);
    setBearbeitungsRaw('');
    setAuswahl(null);
    setHervorgehoben(null);
    setHinweis(null);
    anmerkungenZuruecksetzen();
    setEntwurfFehler(false);
    setEntwurfLoeschenFehler(false);
    // Ein spaet aufloesender Schreibvorgang (Autosave, Uebernehmen, Korrektur aus
    // `blockAbbrechen`) aus dem VERLASSENEN Lauf/Runde darf `entwurfFehler` fuer den NEUEN
    // nicht mehr setzen -- `entwurfSchreiben` prueft dafuer `laufToken.current === meinToken`
    // je Schreibauftrag. `laufToken` zaehlt die Cleanup-Funktion unten bei jeder Aenderung der
    // Abhaengigkeiten hoch (auch N -> null) und dieser Effekt oben noch einmal vor jedem Reset,
    // also bei jedem Laufwechsel und jeder neu offenen Runde -- ein eigener Zaehler dafuer
    // eruebrigt sich dadurch.
    // Codex-Gate-Befund HOCH (Fix-Runde 6): `entwurfWirdVerworfen` sperrt die Bearbeitung
    // (`bearbeitbar` unten) und wird von `entwurfVerwerfen` nur nach einem laufToken-
    // Vergleich zurueckgesetzt (siehe dort) -- bei einem ECHTEN Laufwechsel WAEHREND ein
    // `entwurfVerwerfen` fuer den alten Lauf noch laeuft, greift dieser Vergleich absichtlich
    // NICHT mehr, sonst bliebe die Bearbeitung fuer den neuen Lauf bis zu dessen (belanglosem)
    // Abschluss gesperrt.
    setEntwurfWirdVerworfen(false);
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }

    setTextFeedback('');
    setBildValidierung(null);
    setSendeFehler(null);

    // Laufwechsel zu einem Lauf ohne offene Runde: nur zuruecksetzen, nichts laden.
    if (offen === null) {
      return () => {
        laufToken.current += 1;
      };
    }

    // Ruling 13: ein Entwurf zaehlt nur, solange der Lauf (wie hier) auf eine Freigabe
    // wartet -- zu einem entschiedenen Lauf ist er bedeutungslos.
    // `offen !== null` heisst schon `zustand.art === 'daten'`; TypeScript sieht das nicht.
    const entwurfRoh = zustand.art === 'daten' ? (zustand.auftrag?.entwurf ?? null) : null;
    // MITTEL-4 (Task-13-Review): ein leerer Entwurf ist kein gueltiger Zwischenstand --
    // ohne diese Wache zeigte die Seite eine leere, absendbare Form (derselbe Fehler,
    // den `TEXT_LEER_TEXT` fuer den Text aus n8n schon abfaengt, hier nur fuer den
    // Entwurf). Ein solcher Entwurf wird wie "keiner vorhanden" behandelt.
    const entwurf = entwurfRoh && entwurfRoh.text.trim() !== '' ? entwurfRoh : null;
    if (entwurfRoh && !entwurf) {
      // Task-13-Re-Review NEU-7: MITTEL-4 loest das nur CLIENTSEITIG (der leere Entwurf
      // gilt hier als "keiner vorhanden") -- ohne diesen Aufruf bliebe das leere Feld
      // fuer immer in Firestore liegen, weil das Hinweisband (und mit ihm der einzige
      // Knopf, der es serverseitig loeschen koennte) fuer einen leeren Entwurf gar nicht
      // erst erscheint. `still: true` (Task-13-Re-Review NEU-11, uebertragen auf den
      // Mutex): schlaegt es fehl, bleibt das leere Feld liegen wie bisher -- kein neuer,
      // SICHTBARER Fehlerzustand fuer eine Aufraeumaktion im Hintergrund. Ueber denselben
      // Mutex wie jeder andere Schreibauftrag (statt eines eigenen, direkten Aufrufs) gilt
      // "nie zwei Schreibvorgaenge zum Entwurf gleichzeitig unterwegs" ausnahmslos.
      planeEntwurfSchreiben({
        art: 'loeschen',
        runId,
        runde: offen,
        still: true,
      });
    }

    holen(runId).then(
      (antwort) => {
        if (laufToken.current !== meinToken) return;
        if (antwort.text.trim() === '') {
          setLadeFehler(TEXT_LEER_TEXT);
          return;
        }
        setGeladenFuer({ runId, runde: offen });
        setTextAntwort(antwort);
        const ausN8n = schneide(antwort.text);
        setUrSegmente(ausN8n);
        if (entwurf) {
          // Ruling 34: die Segment-Zuordnung wird GELESEN (`entwurf.segmentierung`),
          // nicht mehr erraten -- nur wenn sie fehlt (Altbestand von VOR diesem Ruling)
          // oder nicht mehr zusammenpasst, faellt `segmentierungEinlesen` auf die alte
          // Ankersuche zurueck (siehe dort und der Kommentar an `schneideAnUrsprung` in
          // `markdown-segmente.ts` fuer deren verbleibendes, dort bewusst belassenes
          // Restrisiko). Ruling 20 (HOCH-1) gilt fuer BEIDE Wege unveraendert: NICHT
          // `schneide(entwurf.text)` -- ein unabhaengiger Neuschnitt wuerde eine im
          // Entwurf zusaetzlich eingefuegte Ueberschrift als eigenes Segment zaehlen und
          // die Zuordnung Index -> Abschnitts-ID gegen `urSegmente`/`struktur.abschnitte`
          // verschieben.
          const geschnitten =
            segmentierungEinlesen(entwurf.segmentierung, entwurf.text, antwort.struktur) ??
            schneideAnUrsprung(entwurf.text, ausN8n);
          setzeSegmente(geschnitten.segmente);
          setHerkunft(geschnitten.herkunft);
          // Ein Zwischenstand von vor den Anmerkungen traegt das Feld nicht.
          setAnmerkungen(entwurf.anmerkungen ?? []);
          // Keine eigene Textentscheidung mehr (Spec B, Abschnitt 6): Wiederhergestellte
          // Anmerkungen machen daraus von selbst eine Korrektur (`leiteEntscheidungAb`).
          setEntwurfHinweis({ gespeichertAm: entwurf.gespeichertAm });
        } else {
          setzeSegmente(ausN8n);
          // Ohne Entwurf ist die Zuordnung trivial: Segment i steht fuer genau
          // `ausN8n`-Index i, keine Verschmelzung.
          setHerkunft(ausN8n.map((_, i) => [i]));
          setEntwurfHinweis(null);
        }
        setBildEntscheidungen(
          antwort.bilder.map((bild) => ({ index: bild.index, aktion: 'approve', feedback: '' })),
        );
      },
      (fehler: unknown) => {
        if (laufToken.current !== meinToken) return;
        setLadeFehler(fehler instanceof ApiError ? fehler.text : ALLGEMEINER_FEHLERTEXT);
      },
    );

    return () => {
      laufToken.current += 1;
    };
  }, [holen, runId, offen, wiederholVersuch]);

  // Verhindert einen Entwurfs-Speicherversuch fuer eine bereits verlassene Seite.
  useEffect(
    () => () => {
      if (entwurfTimer.current) clearTimeout(entwurfTimer.current);
    },
    [],
  );

  // Punkt C des Auftrags (Tab-Wechsel): einmalig abonniert (leere Abhaengigkeitsliste), damit
  // nicht bei jedem Tastenanschlag neu an- und abgemeldet wird. Zeigt ueber die Ref auf die
  // JEWEILS aktuelle `entwurfSofortSichern`-Funktion (wird unten bei jedem Render neu
  // zugewiesen) -- sonst saehe dieser Handler dauerhaft die Werte vom ERSTEN Render.
  useEffect(() => {
    function beiSichtbarkeitswechsel() {
      if (document.hidden) entwurfSofortSichernRef.current();
    }
    document.addEventListener('visibilitychange', beiSichtbarkeitswechsel);
    return () => document.removeEventListener('visibilitychange', beiSichtbarkeitswechsel);
  }, []);

  // Dieselbe Zuordnung Segment -> Abschnitt wie `segmentierungFuer` und
  // `bearbeiteteAbschnitte`; ein verschmolzenes Segment ankert an seinem ersten Abschnitt.
  const abschnittIds = useMemo(() => {
    const struktur = textAntwort?.struktur;
    return segmente.map((_, i) => {
      const ursprung = (herkunft[i] ?? [i])[0];
      return ursprung === undefined ? undefined : struktur?.abschnitte[ursprung]?.abschnittId;
    });
  }, [segmente, herkunft, textAntwort]);
  const zerlegung = useMemo(() => zerlegeInBloecke(zusammen(segmente)), [segmente]);
  const grenzen = useMemo(() => segmentGrenzen(segmente), [segmente]);
  // Ohne Anmerkung und ohne offene Markierung gibt es nichts zu pruefen, also auch kein zweites
  // Rendern jedes Blocks. Memoisiert, weil `abschnittsTexte` alle Bloecke rendert (Uebergabe
  // B1 -> B2, Abschnitt 2).
  const ankerZuPruefen = anmerkungen.length > 0 || offeneMarkierung !== null;
  const abschnittsTexteJeId = useMemo(
    () =>
      ankerZuPruefen
        ? abschnittsTexte(zerlegung, grenzen, abschnittIds)
        : new Map<string, string>(),
    [ankerZuPruefen, zerlegung, grenzen, abschnittIds],
  );
  const ungueltig = useMemo(
    () => new Set(ungueltigeAnmerkungen(anmerkungen, abschnittsTexteJeId)),
    [anmerkungen, abschnittsTexteJeId],
  );
  const gueltigeAnmerkungen = useMemo(
    () => anmerkungen.filter((a) => !ungueltig.has(a)),
    [anmerkungen, ungueltig],
  );

  // Die Auswahl gilt nur fuer den Textstand, auf dem sie gemessen wurde (`text-stand.ts`, R14-4):
  // Ihre Blockindizes zeigen nach "Rückgängig", "Fassung aus n8n laden" oder einer Bearbeitung,
  // die die Blockzahl aendert, auf fremde Bloecke. Geprueft wird hier, beim Ableiten, statt sie
  // bei jedem dieser Ereignisse abzuraeumen; alles Weitere (Leiste, Entfernen, Kommentieren,
  // Entf-Taste) sieht nur `gueltigeAuswahl`. Das offene Kommentarfeld haengt nicht an ihr, es
  // traegt Anker, Stempel und Position selbst (`OffeneMarkierung`).
  const gueltigeAuswahl = auswahl && giltFuer(auswahl, textStand) ? auswahl : null;

  // Entf/Rueck bei stehender Auswahl entfernen (Spec Abschnitt 4.4) -- nie in einem Eingabefeld.
  useEffect(() => {
    if (!gueltigeAuswahl || offeneMarkierung) return;
    const beiTaste = (ereignis: KeyboardEvent) => {
      if (ereignis.key !== 'Delete' && ereignis.key !== 'Backspace') return;
      const ziel = ereignis.target;
      if (ziel instanceof Element && ziel.closest('textarea, input, [contenteditable="true"]'))
        return;
      // Ein offener modaler Dialog (Lupe) verdeckt die Flaeche: Die Auswahl dahinter steht noch,
      // wird aber nicht mehr gesehen. Nicht am Ziel pruefen -- der Fokus liegt im Dialog auch mal
      // auf `body` (Spec 2026-09-30-lupe-bilder, Gesamt-Review Befund 1).
      if (document.querySelector('dialog[open]')) return;
      // Nur, solange genau die gemeldete Auswahl sichtbar steht (I-1, R14-4): nicht nach einer
      // Auswahl ausserhalb, nicht nach einer per Tastatur veraenderten, nicht ohne jede Auswahl.
      if (!auswahlStehtNoch(gueltigeAuswahl.bereich)) return;
      ereignis.preventDefault();
      entfernenRef.current();
    };
    document.addEventListener('keydown', beiTaste);
    return () => document.removeEventListener('keydown', beiTaste);
  }, [gueltigeAuswahl, offeneMarkierung]);

  useEffect(
    () => () => {
      if (sprungzielTimer.current) clearTimeout(sprungzielTimer.current);
      if (sprungzielLauscher.current) {
        window.removeEventListener('scroll', sprungzielLauscher.current);
      }
      if (sprungzielRahmen.current) {
        zeichneBereiche(sprungzielRahmen.current, [], holeRegistrierung(), SPRUNGZIEL);
      }
    },
    [],
  );

  // M6: Mit dem Hinweis verschwindet der Knopf "Rückgängig", und der Fokus fiele auf `body`. Er
  // geht auf den wiederhergestellten Block, sonst auf die Textflaeche. Layout-Effekt, damit der
  // Fokus schon vor dem Malen steht.
  useLayoutEffect(() => {
    const blockIndex = fokusNachRueckgaengig.current;
    const rahmen = flaechenRahmen.current;
    if (blockIndex === null || !rahmen) return;
    fokusNachRueckgaengig.current = null;
    const block = rahmen.querySelector<HTMLElement>(`[data-block-index="${blockIndex}"]`);
    (block && block.tabIndex >= 0 ? block : rahmen).focus();
  }, [textStand]);

  if (zustand.art === 'laedt') return <LoadingState text="Auftrag wird geladen …" />;
  if (zustand.art === 'fehler') {
    return <ErrorState text={zustand.text} onWiederholen={zustand.wiederholen} />;
  }

  const auftrag = zustand.auftrag;
  if (!auftrag) {
    return (
      <EmptyState
        titel="Dieser Auftrag existiert nicht oder gehört zu einem anderen Konto."
        text="Prüfen Sie den Verweis, über den Sie hierher gekommen sind."
      >
        <Link className="eds-button eds-button--primary" to="/">
          Zur Auftragsliste
        </Link>
      </EmptyState>
    );
  }
  const original = originalQuelle(auftrag, runId);

  // Spec 2026-09-29-absende-rennen Abschnitt 5.3: Gesperrt und eine Entscheidung liegt vor (die eigene oder
  // laut Firestore) -> die Uebergabe-Schicht, ueber der ausgegrauten Flaeche, falls Text geladen ist.
  // Steht VOR der Wache darunter: `in_ueberarbeitung`, `finalisierung` und `fehlgeschlagen` nach
  // einer Entscheidung zeigen die Schicht, nicht den generischen Hinweis.
  // Regel R-C2 (Codex-Runde 2 zu PR 48): Die aus Firestore abgeleitete Art gilt nur, solange die
  // Runde NICHT offen ist. Ist sie offen, stammt `entschiedeneRunde` aus einer frueheren Runde
  // (Runde 2 nach Entscheidung 1 hiesse sonst "Wird überarbeitet ..."), und eine Sperre ueber
  // `veraltet` bekaeme eine Schicht, die der 409-Meldung widerspraeche. Bei offener Runde zeigt
  // also nur das eigene, noch haengende Absenden eine Schicht.
  const art = eigeneAbsendung?.art ?? (offen === null ? schichtArtAusFirestore(auftrag) : null);
  // Der Anspruch in Firestore stammt noch vom eigenen, gescheiterten Absenden (Merker oben): Die
  // Flaeche bleibt gesperrt (`gesperrt`), aber sichtbar und lesbar, mit der Meldung -- keine
  // Schicht, die eine laufende Freigabe verspraeche.
  // Nach einem gescheiterten Senden bleibt `runde` bei N: `erhoeheRunde` laeuft im Backend nur nach
  // einem erfolgreichen POST an n8n (apps/backend/src/runs-route.ts). Der Vergleich mit
  // `auftrag.runde` trifft also die Runde des Merkers (Codex-Runde 1 zu PR 48, HOCH-3).
  const eigenerFehlschlag =
    fehlgeschlagen !== null &&
    fehlgeschlagen.runId === runId &&
    fehlgeschlagen.runde === (auftrag.runde ?? 1) &&
    rundeBeansprucht(auftrag);
  const zeigeSchicht =
    gesperrt && art !== null && auftrag.status !== 'fertig' && !eigenerFehlschlag;
  if (zeigeSchicht && !(textAntwort && antwortAktuell)) {
    // Die Refs unten setzt nur ein voller Render. Spaet laufende Aktionen ("Rückgängig",
    // Entf-Taste) sollen die Sperre sehen, unabhaengig davon, welcher Render zuletzt voll lief.
    gesperrtRef.current = true;
    return (
      <div className={stile.seite}>
        <SeitenKopf auftrag={auftrag} runId={runId} />
        <div className={stile.schichtRahmen}>
          <UebergabeSchicht
            art={art}
            status={auftrag.status}
            runId={runId}
            eigene={eigeneAbsendung !== null}
          />
        </div>
      </div>
    );
  }

  // Der einzige eingebaute Weg hierher (der Knopf "Entwurf pruefen" auf der Detailseite)
  // erscheint nur bei `wartet_auf_freigabe` -- ein direkter/alter Link auf diese URL koennte
  // die Bewertungsform trotzdem fuer einen Lauf zeigen, der laengst weiter ist. Das Backend
  // weist ein Absenden dafuer zwar ab (`pruefeEntscheidung`: `nicht_entscheidbar`), aber erst
  // NACH dem Ausfuellen -- ein Hinweis statt der vollen Form ist der passende Zustand
  // (Codex-Gate-Befund MITTEL, fuenfte Runde).
  if (!zeigeSchicht && auftrag.status !== 'wartet_auf_freigabe') {
    return (
      <EmptyState
        titel="Dieser Lauf wartet nicht auf eine Freigabe."
        text={`Aktueller Status: ${STATUS_TEXT[auftrag.status]}.`}
      >
        <Link className="eds-button eds-button--primary" to={`/auftraege/${runId}`}>
          Zum Auftrag
        </Link>
      </EmptyState>
    );
  }

  const runde = auftrag.runde ?? 1;
  const rundenlimitErreicht = runde > MAX_KORREKTURRUNDEN;
  const deaktiviert = gesperrt;
  // Anmerkungen reisen nur mit einer Korrektur -- ab dem Rundenlimit gaebe es fuer sie
  // keinen Weg mehr hinaus.
  const markierenErlaubt = !deaktiviert && !rundenlimitErreicht;
  // Wie beim Textfeld (Ruling 22): Waehrend "Fassung aus n8n laden" laeuft, loeschte der Reset
  // danach eine neue Markierung still. Die Spalte bleibt dabei stehen.
  const markierenMoeglich = markierenErlaubt && !entwurfWirdVerworfen;
  const bearbeitbar = !deaktiviert && !entwurfWirdVerworfen;
  const markierbar = markierenMoeglich && abschnittIds.some((id) => id !== undefined);
  // Die eine Freigabe fuer den Kommentar am ganzen Block: Leiste und Handler lesen beide nur
  // sie. Am Anmerkungslimit gibt es keine Sprechblase mehr, statt erst nach dem Schliessen des
  // offenen Blocks abgewiesen zu werden.
  const blockKommentierbar = markierbar && kannHinzufuegen(anmerkungen);
  const offenerHinweis = offeneMarkierung !== null && hinweisText.trim() !== '';
  const ableitung = leiteEntscheidungAb({
    anmerkungen: anmerkungen.length,
    offenerHinweis,
    gesamtFeedback: textFeedback,
    bilder: bildEntscheidungen,
    rundenlimitErreicht,
  });
  const bilder: TextBild[] = textAntwort?.bilder ?? [];
  const headerBild = bilder.find(istHeaderBild);
  const galerieBilder = bilder.filter((b) => b !== headerBild);
  const entscheidungFuer = (bild: TextBild): BildEntscheidung =>
    bildEntscheidungen.find((e) => e.index === bild.index) ?? {
      index: bild.index,
      aktion: 'approve',
      feedback: '',
    };
  const bewertung = (bild: TextBild, variante: 'hero' | 'galerie') => (
    <BildBewertung
      key={bild.index}
      bild={bild}
      name={bildName(bild, bilder.indexOf(bild))}
      entscheidung={entscheidungFuer(bild)}
      rundenlimitErreicht={rundenlimitErreicht}
      deaktiviert={deaktiviert}
      variante={variante}
      aendern={(aenderung) => bildAendern(bild.index, aenderung)}
    />
  );
  const bilderZurOptimierung = bildEntscheidungen
    .filter((e) => e.aktion === 'optimize')
    .map((e) => {
      const bild = bilder.find((b) => b.index === e.index);
      return {
        index: e.index,
        name: bild ? bildName(bild, bilder.indexOf(bild)) : `Bild ${e.index + 1}`,
        feedback: e.feedback,
        // Dieselbe Drive-Vorschau wie am Bild selbst (R13-4); die Spalte prueft die Quelle.
        ...(bild?.vorschauUrl ? { vorschauUrl: bild.vorschauUrl } : {}),
      };
    });
  const rueckmeldungsZahl =
    anmerkungen.length + bilderZurOptimierung.length + (textFeedback.trim() !== '' ? 1 : 0);
  // Gezaehlt im Text, wie er gerade dasteht: `segmente` traegt einen geladenen Entwurf, ein offener
  // Block zaehlt mit seinem bearbeiteten Stand. Ohne `ersetzeBlock`, das bei veralteter Zerlegung
  // wirft (`pruefeZerlegung`) -- hier genuegt die Differenz der Zaehlungen.
  const offenerRaw = offenerBlock !== null ? zerlegung.bloecke[offenerBlock]?.raw : undefined;
  const offenePruefhinweise =
    zaehlePruefhinweise(zusammen(segmente)) +
    (offenerRaw === undefined
      ? 0
      : zaehlePruefhinweise(bearbeitungsRaw) - zaehlePruefhinweise(offenerRaw));
  const kommentierenAngebot: { erlaubt: true } | { erlaubt: false; grund: string } | null =
    !markierbar
      ? null
      : gueltigeAuswahl?.anker === 'grenze'
        ? { erlaubt: false, grund: 'Markierung reicht über zwei Abschnitte' }
        : gueltigeAuswahl?.anker
          ? { erlaubt: true }
          : { erlaubt: false, grund: 'Diese Stelle lässt sich nicht kommentieren.' };
  // Eine Meldung steht dort, wo gerade gearbeitet wird: an der Auswahl-Leiste, solange sie
  // einen Gegenstand hat (offenes Feld oder gueltige Auswahl), sonst in der Rueckmeldespalte.
  const leisteHatGegenstand = offeneMarkierung !== null || gueltigeAuswahl !== null;
  const fehlerOrt = anmerkungsFehler
    ? anmerkungsFehler.ort !== 'liste' && leisteHatGegenstand
      ? 'leiste'
      : 'liste'
    : null;
  const fehlerMeldung = (ort: 'leiste' | 'liste') =>
    anmerkungsFehler && fehlerOrt === ort ? (
      <p className="eds-field__hint" role="alert">
        {anmerkungsFehler.text}
      </p>
    ) : null;

  function markierungAbgewiesen(text: string) {
    setAnmerkungsFehler({ text, ort: 'feld' });
  }

  function markiert(anker: TextAnker, gemessen: Auswahl) {
    if (!kannHinzufuegen(anmerkungen)) {
      markierungAbgewiesen(`Höchstens ${MAX_ANMERKUNGEN} Anmerkungen je Runde.`);
      return;
    }
    // Das Backend weist ein laengeres Zitat ab -- hier mit Grund statt spaeter beim Absenden.
    if (anker.zitat.length > MAX_ZITAT_ZEICHEN) {
      markierungAbgewiesen(
        `Die markierte Stelle ist zu lang (höchstens ${MAX_ZITAT_ZEICHEN} Zeichen).`,
      );
      return;
    }
    setAnmerkungsFehler(null);
    // Der Hinweistext bleibt stehen: Wer die Auswahl nachbessert, soll nicht neu tippen.
    setOffeneMarkierung({ anker, stand: gemessen.stand, position: gemessen.position });
  }

  function markierungSchliessen() {
    setOffeneMarkierung(null);
    setHinweisText('');
    setAnmerkungsFehler(null);
    setAuswahl(null);
    window.getSelection()?.removeAllRanges();
  }

  // I-2: Das Feld kann mehrere Textstaende ueberleben (gewollt seit T14-Fix I1). Sein Anker gilt
  // dann nur, wenn sein Zitat noch an denselben Offsets steht -- geprueft dort, wo er wirkt.
  function markierungVeraltetMelden() {
    setAnmerkungsFehler({ text: MARKIERUNG_VERALTET_TEXT, ort: 'feld' });
    // Das Feld kann weit ueber dem Hauptknopf stehen -- der Fokus holt es in den Blick.
    hinweisFeldRef.current?.focus();
  }

  function anmerkungUebernehmen() {
    if (!offeneMarkierung) return;
    const markierung = offeneMarkierung;
    if (!giltFuer(markierung, textStand, () => ankerGilt(markierung.anker, abschnittsTexteJeId))) {
      markierungVeraltetMelden();
      return;
    }
    const kommentar = hinweisText.trim();
    if (kommentar === '') {
      setAnmerkungsFehler({
        text: 'Bitte einen Hinweis zur markierten Stelle schreiben.',
        ort: 'feld',
      });
      return;
    }
    // Wer eine Anmerkung uebernimmt, meint eine Korrektur -- das leitet `leiteEntscheidungAb`
    // jetzt selbst ab. Ab dem Rundenlimit ist Markieren ohnehin aus.
    anmerkungenGeaendert([...anmerkungen, neueAnmerkung(markierung.anker, kommentar)]);
    markierungSchliessen();
  }

  function anmerkungEntfernen(anmerkung: Anmerkung) {
    anmerkungenGeaendert(anmerkungen.filter((a) => a !== anmerkung));
    setAnmerkungsFehler(null);
  }

  // Eine Listenaenderung laeuft ueber dieselbe Entprellung wie eine Texteingabe und nimmt eine
  // offene Bearbeitung mit, sonst ueberschriebe sie deren schon gesicherten Text. Die neue Liste
  // reist ausdruecklich mit: `anmerkungen` dieses Renders ist noch die alte.
  function anmerkungenGeaendert(neu: Anmerkung[]) {
    setAnmerkungen(neu);
    anmerkungenUngesichert.current = true;
    if (entwurfTimer.current) clearTimeout(entwurfTimer.current);
    planeAutosave(offenerBlock, bearbeitungsRaw, neu);
  }

  // Springt zur markierten Passage und laesst sie kurz aufblinken. Der Listeneintrag wird NICHT
  // hervorgehoben (R14-3): Spec Abschnitt 4.4 bindet Hervorheben und Scrollen der Liste an den Klick auf
  // die eingefaerbte Stelle im Text (`onAnmerkungGetroffen`). Das Aufblinken ist eine eigene
  // Registrierung ('awf-sprungziel') und fuegt keinen Textknoten ein.
  function zurStelle(anmerkung: Anmerkung) {
    const rahmen = flaechenRahmen.current;
    const { abschnittId, von, bis, zitat } = anmerkung.anker;
    // Eine ungueltige Anmerkung zeigt auf keine Stelle mehr: dort koennten von/bis auf anderen
    // Text fallen, also gar keine Range bilden.
    const bereich =
      rahmen && !ungueltig.has(anmerkung)
        ? bereichImAbschnitt(rahmen, abschnittId, von, bis)
        : null;
    // Leerraum vergleichen wir nicht wortgenau: Ein Umbruch oder doppeltes Leerzeichen im
    // Zitat liess den Sprung sonst auf den Abschnittsanfang zurueckfallen.
    const gleich = (x: string) => x.replace(/\s+/g, ' ').trim();
    if (rahmen && bereich && gleich(bereich.toString()) === gleich(zitat)) {
      const rand = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      // Unter dem sticky Kopf (4rem) mit etwas Luft, nicht darunter verdeckt.
      const abstand = 6 * rand;
      const weg = bereich.getBoundingClientRect().top - abstand;
      window.scrollBy({ top: weg, behavior: bildlaufVerhalten() });
      // Nachkorrektur nach dem Bildlauf (Rueckmeldung 2026-10-13: "teilweise muss man noch
      // scrollen"): Bilder ueber der Stelle laden erst beim Vorbeiscrollen nach und schieben sie
      // weiter nach unten. Steht sie danach nicht im Sichtfeld, wird einmal ohne Animation
      // nachgezogen. `scrollend` statt Zeitgrenze: feuert genau, wenn der Bildlauf steht.
      // Ohne Bewegung kein `scrollend`: dann gibt es nichts nachzuziehen, und ein haengender
      // Listener sprang sonst beim naechsten eigenen Scrollen zurueck (Codex-Gate PR #70). Eine
      // Zeitgrenze raeumt ihn auch ab, wenn der Browser das Ereignis gar nicht kennt.
      nachzugAbbau.current?.();
      nachzugAbbau.current = null;
      if (Math.abs(weg) >= 1) {
        const nachziehen = () => {
          abbauen();
          const r = bereich.getBoundingClientRect();
          if (r.top < 4 * rand || r.bottom > window.innerHeight) {
            window.scrollBy({ top: r.top - abstand, behavior: 'instant' });
          }
        };
        const grenze = window.setTimeout(() => abbauen(), 3000);
        const abbauen = () => {
          window.clearTimeout(grenze);
          window.removeEventListener('scrollend', nachziehen);
          nachzugAbbau.current = null;
        };
        nachzugAbbau.current = abbauen;
        window.addEventListener('scrollend', nachziehen);
      }
      blinkeAuf(rahmen, bereich);
      return;
    }
    // Rueckfall: zum Anfang des Abschnitts (jsdom kennt `scrollIntoView` nicht).
    const ziel = Array.from(document.querySelectorAll('[data-abschnitt-id]')).find(
      (element) => element.getAttribute('data-abschnitt-id') === abschnittId,
    );
    if (typeof ziel?.scrollIntoView === 'function') {
      ziel.scrollIntoView({ behavior: bildlaufVerhalten(), block: 'center' });
    }
  }

  // "Zum Bild" aus der Rueckmeldespalte: Das Feld steht nur am Bild. Ist es zugeklappt (Feedback
  // steht, Fokus ist weg), oeffnet es der Knopf "Wird geändert", der dabei auch fokussiert.
  function zumBild(index: number) {
    const figur = document.querySelector<HTMLElement>(`figure[data-bild-index="${index}"]`);
    if (!figur) return;
    if (typeof figur.scrollIntoView === 'function') {
      figur.scrollIntoView({ behavior: bildlaufVerhalten(), block: 'center' });
    }
    const feld = figur.querySelector('textarea');
    if (feld) feld.focus({ preventScroll: true });
    else figur.querySelector<HTMLButtonElement>('[data-feld-oeffnen]')?.click();
  }

  // Springt zum n-ten Pruefhinweis im Text und laesst ihn aufblinken. Ein gerade bearbeiteter
  // Block zeigt seine Hinweise nicht als Chip; stimmt die Zahl der Chips dann nicht mit der
  // gezaehlten ueberein, waere der n-te Chip ein anderer Hinweis (Codex-Gate PR #70), und der
  // Sprung bleibt aus.
  function zumPruefhinweis(index: number) {
    const rahmen = flaechenRahmen.current;
    const chips = rahmen?.querySelectorAll("[data-marker='pruefhinweis']");
    if (!rahmen || !chips || chips.length !== offenePruefhinweise) return;
    const chip = chips[index];
    if (!chip) return;
    if (typeof chip.scrollIntoView === 'function') {
      chip.scrollIntoView({ behavior: bildlaufVerhalten(), block: 'center' });
    }
    const bereich = document.createRange();
    bereich.selectNodeContents(chip);
    blinkeAuf(rahmen, bereich);
  }

  function anmerkungGetroffen(anmerkung: Anmerkung) {
    setHervorgehoben(anmerkung);
    setHervorhebungsAnlass((n) => n + 1);
  }

  function blinkeAuf(rahmen: Element, bereich: Range) {
    if (sprungzielTimer.current) clearTimeout(sprungzielTimer.current);
    if (sprungzielLauscher.current) {
      window.removeEventListener('scroll', sprungzielLauscher.current);
    }
    sprungzielRahmen.current = rahmen;
    zeichneBereiche(rahmen, [bereich], holeRegistrierung(), SPRUNGZIEL);
    const beginn = Date.now();
    const beende = () => {
      window.removeEventListener('scroll', planeEnde);
      sprungzielLauscher.current = null;
      sprungzielTimer.current = null;
      zeichneBereiche(rahmen, [], holeRegistrierung(), SPRUNGZIEL);
    };
    // Jedes Scroll-Ereignis schiebt das Ende hinaus, bis die Obergrenze erreicht ist.
    function planeEnde() {
      if (sprungzielTimer.current) clearTimeout(sprungzielTimer.current);
      const rest = SPRUNGZIEL_HOECHSTENS_MS - (Date.now() - beginn);
      sprungzielTimer.current = setTimeout(beende, Math.max(0, Math.min(SPRUNGZIEL_MS, rest)));
    }
    sprungzielLauscher.current = planeEnde;
    window.addEventListener('scroll', planeEnde, { passive: true });
    planeEnde();
  }

  function bildAendern(index: number, aenderung: Partial<BildEntscheidung>) {
    setBildEntscheidungen((alt) =>
      alt.map((b) => (b.index === index ? { ...b, ...aenderung } : b)),
    );
    setBildValidierung(null);
  }

  // Der Blockindex eines Klicks ist in `TextFlaeche.klick` gegen den Textstand beim Druck
  // geprueft (M4); hier kommt nur ein Index des gezeigten Stands an.
  function blockOeffnen(blockIndex: number) {
    const block = zerlegung.bloecke[blockIndex];
    // Rueckuebersetzung und `ersetzeBlock` nur fuer editierbare Bloecke (Uebergabe B1 -> B2,
    // Abschnitt 2) -- hier noch einmal geprueft, statt nur in den Aufrufern (R14-8).
    if (!block?.editierbar) return;
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }
    // Der eben abgeraeumte Timer kann eine Listenaenderung getragen haben.
    if (anmerkungenUngesichert.current) planeSpeichern(segmente);
    // Ein offenes Kommentarfeld bleibt stehen (Fix-Runde 1, I1): Es haengt nicht an der Auswahl.
    setAuswahl(null);
    setOffenerBlock(blockIndex);
    setBearbeitungsRaw(block.raw);
    // Neue Bearbeitungssitzung -- noch keine Speicherung darin (Ruling 22, HOCH-4).
    sitzungGespeichert.current = false;
    // Noch keine Aenderung in dieser Sitzung -- das blosse Oeffnen zaehlt nicht (siehe
    // `blockEingabe`).
    entwurfUngesichert.current = false;
    // `entwurfFehler` bewusst NICHT hier zuruecksetzen (Task-13-Review MITTEL-1): sonst
    // verschwaende ein noch unbehobener Speicherfehler beim blossen Oeffnen eines Blocks,
    // ohne dass tatsaechlich neu gespeichert wurde.
  }

  /**
   * Ruling 34: die Segment-Zuordnung EINES konkreten Speicherstands -- gebaut aus dem
   * aktuellen `herkunft` (State oben) und der `struktur` der zuletzt von n8n geladenen
   * Fassung. `herkunft[i]` bleibt fuer `zielSegmente[i]` gueltig, solange `zielSegmente`
   * `segmente` selbst ist oder durch `ersetzeBlock()`/`entferneBloecke()` daraus
   * hervorgegangen ist (so bei jedem Aufruf ueber `planeSpeichern`) -- beide aendern nie
   * Segmentanzahl oder -reihenfolge, nur `markdown` (ein geleertes Segment bleibt stehen).
   * Liefert ein LEERES Objekt (statt `{ segmentierung: undefined }`, das
   * `exactOptionalPropertyTypes` ablehnt), wenn `struktur` fehlt (z. B. ein Auftragstyp ohne
   * Struktur) -- direkt in den Auftrag hineinspreadbar. Der Auftrag geht dann einfach ohne
   * `segmentierung` hinaus, wie ein Client, der Ruling 34 noch nicht kennt --
   * `entwurfHandler` im Backend behandelt das Feld bewusst als optional.
   */
  function segmentierungFuer(
    zielSegmente: Segment[],
  ): { segmentierung: EntwurfSegmentEintrag[] } | Record<string, never> {
    const struktur = textAntwort?.struktur;
    if (!struktur) return {};
    return {
      segmentierung: zielSegmente.map((segment, i) => ({
        abschnittIds: (herkunft[i] ?? [i])
          .map((idx) => struktur.abschnitte[idx]?.abschnittId)
          .filter((id): id is string => typeof id === 'string'),
        laenge: segment.markdown.length,
      })),
    };
  }

  // Der einzige Weg zu einem `speichern`-Auftrag. Der Server ersetzt den Zwischenstand als
  // Ganzes, die Liste reist deshalb immer mit, auch leer.
  function planeSpeichern(zielSegmente: Segment[], liste: Anmerkung[] = anmerkungen): void {
    anmerkungenUngesichert.current = false;
    planeEntwurfSchreiben({
      art: 'speichern',
      runId,
      runde,
      text: zusammen(zielSegmente),
      ...segmentierungFuer(zielSegmente),
      anmerkungen: liste,
    });
  }

  function blockAbbrechen(_blockIndex: number) {
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }
    // Ruling 22 (HOCH-4): ein bereits abgeschickter oder abgeschlossener Autosave aus
    // DIESER Sitzung bleibt serverseitig bestehen, obwohl die Bearbeitung hier ausdruecklich
    // abgebrochen wird -- ohne Korrektur wuerde der naechste Ladevorgang die verworfene
    // Aenderung wieder anzeigen. `segmente` (ohne die gerade abgebrochene Aenderung, die nie
    // dort ankam) ist der massgebliche, korrekte Stand: entspricht er `urSegmente`, gibt es
    // nichts mehr zu bewahren -- loeschen; sonst den korrekten Stand erneut speichern, um den
    // durch den abgebrochenen Autosave verunreinigten Entwurf zu ueberschreiben.
    // Anmerkungen sind auch zum Text aus n8n ein Zwischenstand, der bleiben muss.
    if (sitzungGespeichert.current || anmerkungenUngesichert.current) {
      sitzungGespeichert.current = false;
      const korrekterStand = zusammen(segmente);
      if (korrekterStand === zusammen(urSegmente) && anmerkungen.length === 0) {
        anmerkungenUngesichert.current = false;
        planeEntwurfSchreiben({ art: 'loeschen', runId, runde });
      } else {
        planeSpeichern(segmente);
      }
    }
    setOffenerBlock(null);
  }

  // Gemeinsamer Kern des Sofort-Schreibens -- genutzt vom Debounce-Timer unten UND (Punkt C
  // des Auftrags) von `entwurfSofortSichern`, wenn `visibilitychange` den Autosave ohne
  // Wartezeit ausloest. Beide Aufrufstellen brauchen exakt denselben Schreibweg.
  // Ohne offenen Block (`blockIndex` null) geht der uebernommene Stand samt Liste hinaus.
  function entwurfJetztSpeichern(blockIndex: number | null, raw: string, liste = anmerkungen) {
    if (blockIndex === null) {
      planeSpeichern(segmente, liste);
      return;
    }
    sitzungGespeichert.current = true;
    // Ab hier auf dem Weg -- ein nachfolgendes `visibilitychange` hat nichts mehr
    // vorzuziehen, bis die naechste echte Aenderung `entwurfUngesichert` wieder setzt.
    entwurfUngesichert.current = false;
    // Die CRLF-Garantie aus Ruling 7 haelt jetzt `wendeAn` (normalisiert `neu`).
    planeSpeichern(ersetzeBlock(segmente, zerlegung, blockIndex, raw), liste);
  }

  function planeAutosave(blockIndex: number | null, raw: string, liste = anmerkungen) {
    entwurfTimer.current = setTimeout(() => {
      entwurfTimer.current = null;
      entwurfJetztSpeichern(blockIndex, raw, liste);
    }, ENTWURF_VERZOEGERUNG_MS);
  }

  // Der Timer entsteht bewusst HIER, im Eingabe-Handler, statt in einem `useEffect` ueber
  // `bearbeitungsRaw`: Ein Effekt liefe schon beim OEFFNEN eines Blocks an -- es gibt zu
  // diesem Zeitpunkt aber noch gar keine "letzte Eingabe", ab der 2s gezaehlt werden koennten.
  function blockEingabe(blockIndex: number, raw: string) {
    setBearbeitungsRaw(raw);
    if (entwurfTimer.current) clearTimeout(entwurfTimer.current);
    // Eine echte Aenderung -- ab jetzt gibt es etwas zu sichern (siehe `entwurfUngesichert`
    // oben), auch wenn `absenden` den gerade geplanten Timer gleich wieder abbricht.
    entwurfUngesichert.current = true;
    planeAutosave(blockIndex, raw);
  }

  // Punkt C des Auftrags: sofortiges Sichern beim Tab-Wechsel (`visibilitychange`, siehe
  // Effekt oben, der ueber `entwurfSofortSichernRef` hierher zeigt), zusaetzlich zum
  // Entprellen -- nicht ANSTATT. Das Verlassen eines Blocks (`blur`) schliesst ihn und laeuft
  // ueber `blockSchliessen`. Bewusst an `entwurfUngesichert` geprueft, NICHT an "laeuft gerade
  // ein Timer" (`entwurfTimer`): `absenden` bricht dessen Timer bei JEDEM Versuch ab, auch
  // einem gescheiterten (Ruling 24) -- ohne die eigene Markierung wuesste dieser Handler nach
  // einem gescheiterten Absenden nicht mehr, dass der getippte Text noch aussteht.
  function entwurfSofortSichern() {
    if (deaktiviert || entwurfWirdVerworfen) return;
    const textUngesichert = offenerBlock !== null && entwurfUngesichert.current;
    if (!textUngesichert && !anmerkungenUngesichert.current) return;
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }
    entwurfJetztSpeichern(offenerBlock, bearbeitungsRaw);
  }
  entwurfSofortSichernRef.current = entwurfSofortSichern;

  function blockSchliessen(blockIndex: number, raw: string) {
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }
    const vorher = segmente;
    const neu = ersetzeBlock(segmente, zerlegung, blockIndex, raw);
    setOffenerBlock(null);
    // Nur angeklickt und nichts geaendert: kein Speichern, keine Meldung (Review-Fokus 1).
    if (neu === segmente && !sitzungGespeichert.current) return;
    sitzungGespeichert.current = true;
    if (neu !== segmente) {
      const stand = setzeSegmente(neu);
      leerTextHinweisAufheben();
      zeigeRueckgaengig('Änderung übernommen', vorher, stand, blockIndex);
    }
    // Der Speicherversuch startet ERST hier, NACH dem Schliessen -- ein Fehler landet deshalb
    // in der Rueckmeldespalte (Task-13-Review MITTEL-1), nicht an einem Block, der dann schon
    // zu ist. Ein noch ausstehender, aelterer Autosave wird durch diesen neueren ERSETZT.
    planeSpeichern(neu);
  }

  // Der Hinweis "Der Text ist leer" beschreibt einen Stand, den jede Textaenderung ueberholt
  // (Fix-Runde 1, Minor 2). Andere Sendefehler bleiben stehen.
  function leerTextHinweisAufheben() {
    setSendeFehler((fehler) => (fehler === TEXT_ENTFERNT_TEXT ? null : fehler));
  }

  /**
   * "Rückgängig" fuer die letzte Blockaktion (Spec Abschnitt 4.5): stellt `vorher` wieder her, aber nur,
   * solange der Text noch der Stand `nachher` ist, den diese Aktion erzeugt hat
   * (`text-stand.ts`). Jede andere Textaenderung dazwischen macht den Schnappschuss wertlos.
   *
   * M8, bewusst so: Ein Klick auf "Rückgängig" bei offenem, geaendertem Block blurt zuerst. Das
   * Schliessen ist eine eigene Blockaktion mit eigenem Hinweis, der den alten ersetzt, und der
   * Klick nimmt dann DIESE Bearbeitung zurueck, nicht die Entfernung davor -- "nur die letzte
   * Aktion" (Spec Abschnitt 4.5). Festgeschrieben in der Tabelle "Zustand × Ereignis".
   */
  function zeigeRueckgaengig(text: string, vorher: Segment[], nachher: number, fokus: number) {
    const token = laufToken.current;
    hinweisZaehler.current += 1;
    setHinweis({
      id: hinweisZaehler.current,
      text,
      aktion: {
        text: 'Rückgängig',
        ausfuehren: () => {
          // Nach dem Absenden oder einem Laufwechsel darf nichts mehr geschrieben werden
          // (Review-Fokus 4). `laufToken` bleibt neben dem Stempel: Zwischen Laufwechsel und
          // neuem Text steht noch der alte Text mit seinem Stand, und `planeSpeichern` schriebe
          // ihn fuer den neuen Lauf.
          //
          // T-2 (Codex-Fix B2), Controller-Ruling (Task 15, M6): Tiefenschutz OHNE eigenen
          // Oberflaechentest. Ueber die Oberflaeche nicht erreichbar: Nach Erfolg oder
          // Laufwechsel ist der Knopf (samt `Hinweis`) schon entfernt (`setHinweis(null)` bzw.
          // der Reset beim Laufwechsel), waehrend eines laufenden Vorgangs ist er deaktiviert
          // (`gesperrtRef`, sichtbar an `aktionGesperrt`) -- ein Klick darauf loest in keinem
          // der beiden Faelle `onClick` und damit diese Funktion ueberhaupt aus.
          if (laufToken.current !== token || gesperrtRef.current) return;
          if (!giltFuer({ stand: nachher }, standZaehler.current)) return;
          // Ein laufender Timer (etwa aus `anmerkungenGeaendert`) traegt den Stand VOR
          // "Rückgängig" und ersetzte als letzter Auftrag das Speichern unten (R14-5).
          if (entwurfTimer.current) {
            clearTimeout(entwurfTimer.current);
            entwurfTimer.current = null;
          }
          fokusNachRueckgaengig.current = fokus;
          setzeSegmente(vorher);
          setOffenerBlock(null);
          leerTextHinweisAufheben();
          planeSpeichernRef.current(vorher);
        },
      },
    });
  }

  /**
   * Die gemeldete Auswahl am Verwendungsort (Leistenknopf, Entf-Taste): Sie gilt nur fuer ihren
   * Textstand UND nur, solange genau sie noch sichtbar markiert ist (H2, Codex-Runde 2). Aendert
   * sich die DOM-Auswahl ohne Mausvorgang (Umschalt+Pfeil, Touch-Griff), meldet die Flaeche sie
   * erst nach einer kurzen Pause neu; ein Knopfdruck dazwischen wirkt nicht und schliesst die
   * Leiste, statt die unsichtbar gewordene Stelle zu treffen.
   */
  function auswahlZumWirken(): Auswahl | null {
    if (!gueltigeAuswahl) return null;
    if (auswahlStehtNoch(gueltigeAuswahl.bereich)) return gueltigeAuswahl;
    setAuswahl(null);
    return null;
  }

  function bloeckeEntfernen() {
    if (deaktiviert || entwurfWirdVerworfen) return;
    const auswahlHier = auswahlZumWirken();
    if (!auswahlHier || auswahlHier.bloecke.length === 0) return;
    // Dieselbe Freigabe wie das Angebot (`entfernenText`): Eine Auswahl im offenen Block entfernt
    // nichts. Dort waere unklar, ob der getippte oder der gespeicherte Text verschwinden soll.
    if (beruehrtOffenenBlock(auswahlHier)) return;
    entferne(auswahlHier.bloecke);
  }

  function beruehrtOffenenBlock(gemessen: Auswahl): boolean {
    return offenerBlock !== null && gemessen.bloecke.includes(offenerBlock);
  }

  // Die Leiste am Block (Karte j3gkZH2Hgc6K57Oa8f1F): derselbe Weg wie ueber die Markierung, nur
  // ohne DOM-Auswahl. Ein offener Block ist hier schon geschlossen (`TextFlaeche`, `blockWunsch`).
  function blockEntfernen(blockIndex: number) {
    if (deaktiviert || entwurfWirdVerworfen) return;
    if (blockIndex < 0 || blockIndex >= zerlegung.bloecke.length) return;
    entferne([blockIndex]);
  }

  function entferne(bloecke: number[]) {
    const beschriftung = entfernenBeschriftung(zerlegung, grenzen, bloecke);
    const vorher = segmente;
    const neu = entferneBloecke(segmente, zerlegung, bloecke);
    setAuswahl(null);
    window.getSelection()?.removeAllRanges();
    if (neu === segmente) return;
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }
    const stand = setzeSegmente(neu);
    planeSpeichern(neu);
    // Nach "Rückgängig" steht der erste entfernte Block wieder an seinem Index.
    zeigeRueckgaengig(
      beschriftung.replace(/ entfernen$/, ' entfernt'),
      vorher,
      stand,
      bloecke[0] ?? 0,
    );
  }

  function auswahlGemeldet(neu: Auswahl | null) {
    // Ein offenes Kommentarfeld bleibt stehen, wenn die Auswahl verschwindet (Klick ins Feld).
    // Eine neue Auswahl bei offenem Feld versetzt die Markierung, der Hinweis bleibt.
    if (offeneMarkierung) {
      if (!neu?.anker || neu.anker === 'grenze') return;
      // Im offenen Block gemessen, zeigen die Offsets auf ungesicherten Text: Das Feld bleibt, wo
      // es ist. Ein Schliessen mitten im Tippen, nur um das Feld zu versetzen, waere schlimmer.
      if (beruehrtOffenenBlock(neu)) return;
      if (gleicheStelle(neu.anker, offeneMarkierung.anker)) {
        // Dieselbe Stelle neu vermessen (nach einem neuen Textstand, siehe `TextFlaeche`): nur
        // Stempel, Anker und Position nachziehen. Eine Meldung bleibt, ausser der, dass sich die
        // Stelle geaendert habe -- die gilt jetzt nicht mehr.
        setOffeneMarkierung({ anker: neu.anker, stand: neu.stand, position: neu.position });
        setAnmerkungsFehler((fehler) =>
          fehler?.text === MARKIERUNG_VERALTET_TEXT ? null : fehler,
        );
        return;
      }
      markiert(neu.anker, neu);
      return;
    }
    setAuswahl(neu);
  }

  function kommentieren() {
    // Dieselbe Freigabe wie das Angebot (`kommentierenAngebot`).
    if (!markierbar) return;
    const gemessen = auswahlZumWirken();
    if (!gemessen?.anker || gemessen.anker === 'grenze') return;
    if (offenerBlock !== null && beruehrtOffenenBlock(gemessen)) {
      // Erst den getippten Text uebernehmen, dann die Passage im neuen Textstand wiederfinden
      // (`verankereNachSchliessen`). Der Anker ist am bearbeiteten Text gemessen; seine Offsets
      // gelten erst, wenn genau dieser Text in den Segmenten steht.
      blockSchliessen(offenerBlock, bearbeitungsRaw);
      setAuswahl(null);
      setNachSchliessenVerankern(gemessen.anker);
      return;
    }
    markiert(gemessen.anker, gemessen);
  }

  function verankereNachSchliessen(anker: TextAnker) {
    const rahmen = flaechenRahmen.current;
    const bereich = rahmen ? wiederfinden(rahmen, anker) : null;
    const neu = rahmen && bereich ? ankerImText(rahmen, bereich) : null;
    if (!rahmen || !bereich || !neu || neu === 'grenze') {
      markierungAbgewiesen(MARKIERUNG_VERALTET_TEXT);
      return;
    }
    window.getSelection()?.removeAllRanges();
    markiert(neu, {
      stand: textStand,
      bloecke: [],
      anker: neu,
      position: positionVon(rahmen, bereich),
      bereich,
    });
  }

  // Kommentar zum ganzen Block ueber die Leiste am Block: oeffnet dasselbe Kommentarfeld wie
  // eine Markierung, mit dem Text des Blocks als Zitat. Die Messung kommt aus `TextFlaeche`
  // und gilt nur fuer den Textstand, in dem sie entstand.
  function blockKommentieren(gemessen: Auswahl) {
    if (!blockKommentierbar || !giltFuer(gemessen, textStand)) return;
    if (!gemessen.anker || gemessen.anker === 'grenze') {
      setAnmerkungsFehler({ text: 'Dieser Block lässt sich nicht kommentieren.', ort: 'liste' });
      return;
    }
    setAuswahl(null);
    markiert(gemessen.anker, gemessen);
  }

  async function entwurfVerwerfen() {
    setEntwurfWirdVerworfen(true);
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }
    // Codex-Gate-Befund HOCH (Fix-Runde 6), dieselbe Fehlerklasse wie der
    // `geladenFuer`-Kurzschluss aus Task 12: erfasst VOR dem Warten, damit ein Vergleich
    // danach einen inzwischen verlassenen Lauf/Runde erkennt. Ohne ihn ueberschrieb ein
    // spaet aufloesendes `DELETE` fuer den ALTEN Lauf noch `segmente`, `herkunft` und
    // `offenerBlock` des laengst angezeigten NEUEN Laufs -- der Ladeeffekt oben bumpt
    // `laufToken` bei JEDEM echten Lauf-/Rundenwechsel (seine Cleanup-Funktion), unabhaengig
    // davon, ob dieser Aufruf hier noch laeuft.
    const meinToken = laufToken.current;
    // Gate-Befund HOCH (Neuaufsatz): KEINE Ausnahme mehr, dieses `DELETE` geht durch
    // DIESELBE Warteschlange wie jeder andere Schreibweg (`planeEntwurfSchreiben`). Eine
    // fruehere Fassung schickte es direkt und AUSSERHALB des Mutex los -- ein waehrend des
    // Wartens fuer einen ANDEREN, inzwischen angezeigten Lauf neu geplanter Auftrag blieb
    // dabei fuer immer haengen, weil das direkte `finally` hier `entwurfLaeuft` zwar
    // zuruecksetzte, aber nie abholte, was inzwischen in `entwurfAusstehend` wartete: die
    // Seite speicherte von da an still nie wieder (siehe `entwurfSchreiben` oben, wo GENAU
    // DAS jetzt strukturell ausgeschlossen ist). `planeEntwurfSchreiben` leistet hier
    // unveraendert dasselbe wie vorher von Hand: ein noch nicht gestarteter Autosave wird
    // durch dieses `DELETE` ERSETZT (Punkt B des Auftrags, `entwurfAusstehend` haelt nur den
    // letzten Auftrag), ein BEREITS laufender PUT wird erst zu Ende gefuehrt, dann holt
    // GENAU DIESELBE `while`-Schleife dieses `DELETE` automatisch nach -- ein aelterer PUT
    // kann also weiterhin nicht NACH diesem `DELETE` ankommen und den gerade verworfenen
    // Entwurf wiederauferstehen lassen. `still: true`, weil dieser Auftrag sein Ergebnis
    // ueber `melde` an einen EIGENEN Hinweis meldet (`entwurfLoeschenFehler` unten), nicht an
    // das allgemeine `entwurfFehler`.
    const ergebnis = await new Promise<EntwurfSchreibergebnis>((melde) => {
      planeEntwurfSchreiben({ art: 'loeschen', runId, runde, still: true, melde });
    });
    // Der Ladeeffekt hat `entwurfWirdVerworfen` bei einem echten Laufwechsel bereits selbst
    // zurueckgesetzt (siehe dort) -- dieser Aufruf gehoert dann zu einem VERLASSENEN Lauf und
    // darf dessen (frischen) Zustand nicht mehr anfassen.
    if (laufToken.current !== meinToken) return;
    setEntwurfWirdVerworfen(false);
    if (ergebnis === 'fehler') {
      // HOCH-2 (Task-13-Review): NICHT optimistisch lokal weiterschalten -- sonst zeigte
      // die Seite die Fassung aus n8n, waehrend der Entwurf in Wahrheit weiter in Firestore
      // liegt und beim naechsten Oeffnen zurueckkehrt. Ein Hinweis statt eines stillen,
      // wirkungslosen Knopfdrucks.
      setEntwurfLoeschenFehler(true);
      return;
    }
    setEntwurfLoeschenFehler(false);
    // "Rückgängig" bezieht sich auf einen Stand, den dieses Verwerfen gerade weggeworfen hat
    // (Fix-Runde 1, I2) -- aber NUR im Erfolgsfall: Scheitert das Loeschen, ist nichts
    // verworfen, und der Hinweis bleibt gueltig (Fix-Runde 2). Waehrend das Loeschen laeuft,
    // sperrt `gesperrtRef` (und `aktionGesperrt` am Knopf). Den Hinweis aus dem Blur eines
    // offenen Blocks gibt es hier schon: Der Blur laeuft VOR dem Klick, und waehrend des
    // Verwerfens laesst sich kein Block oeffnen (`bearbeitbar`).
    setHinweis(null);
    leerTextHinweisAufheben();
    setzeSegmente(urSegmente);
    // Trivial nachziehen (siehe Ladeeffekt oben): ohne Entwurf entspricht Segment i wieder
    // genau `urSegmente`-Index i.
    setHerkunft(urSegmente.map((_, i) => [i]));
    setOffenerBlock(null);
    // Die Anmerkungen lagen im geloeschten Zwischenstand. Blieben sie stehen, schriebe die
    // naechste Sicherung sie gegen die Fassung aus n8n zurueck.
    anmerkungenZuruecksetzen();
    // `entwurfHinweis` NICHT mehr hier explizit loeschen: `entwurfSchreiben` (oben) raeumt
    // ihn bei JEDEM erfolgreichen `loeschen`-Auftrag ab, dieser hier eingeschlossen.
  }

  async function absenden(ereignis: FormEvent) {
    ereignis.preventDefault();
    await fuehreAbsendenAus(false);
  }

  /**
   * Der Absendeweg. `pruefhinweiseBestaetigt`: Die Nutzerin hat im Dialog "Trotzdem freigeben"
   * gewaehlt (Spec 2026-10-12 §5.5) -- dann fragt die Seite nicht erneut.
   */
  async function fuehreAbsendenAus(pruefhinweiseBestaetigt: boolean) {
    // `antwortAktuell` reicht als Wache: Ist sie falsch, rendert das Formular (siehe unten)
    // gar nicht erst -- dieser fruehe Ausstieg ist nur die Absicherung fuer den Fall, dass
    // `absenden` trotzdem aufgerufen wird. `entwurfWirdVerworfen` gehoert seit Fix-Runde 6
    // dazu (Codex-Gate-Befund HOCH): ohne diese Zeile liess sich die Entwurfsfassung noch
    // absenden, waehrend "Fassung aus n8n laden" bereits lief -- der `disabled`-Zustand des
    // Knopfs unten allein verhindert keinen programmatischen `submit` des Formulars.
    if (deaktiviert || !textAntwort || !antwortAktuell || entwurfWirdVerworfen) return;
    // Zwei Ausloeser im selben Takt (Enter + Klick) sehen beide noch den Render vor dem ersten
    // `setzeAbsendung` unten, `deaktiviert` haelt den zweiten also nicht auf (Test 1b). Die Ref
    // schon: Sie traegt die Absendung sofort. Hier oben statt direkt vor `senden`, damit der
    // zweite Ausloeser auch keine der Nebenwirkungen bis dahin ausloest.
    if (absendungRef.current?.runId === runId && absendungRef.current.runde === runde) return;

    // Ruling 23 (Gate-Befund HOCH, Fix-Runde 3): ein noch offener, nicht geschlossener Block
    // wuerde sonst beim Absenden still verworfen -- wer tippt und direkt absendet, erwartet,
    // dass sein Text mitgeht. Derselbe Schreibweg wie `blockSchliessen` (`ersetzeBlock`).
    // Bewusst NUR eine lokale Variable fuer die Nutzlast, KEIN `setzeSegmente`/`setOffenerBlock`
    // -- scheitert `senden`, muss die Bearbeitung unveraendert offen stehen bleiben (siehe
    // "scheitert das Absenden, bleibt die Bearbeitung erhalten").
    const segmenteZumAbsenden =
      offenerBlock !== null
        ? ersetzeBlock(segmente, zerlegung, offenerBlock, bearbeitungsRaw)
        : segmente;
    // Das Backend verwirft leeren `ratgeberText` (runs.ts:399) -- hier mit Grund statt spaeter.
    if (zusammen(segmenteZumAbsenden).trim() === '') {
      setSendeFehler(TEXT_ENTFERNT_TEXT);
      return;
    }

    // Derselbe Gedanke wie Ruling 23 fuer ein Kommentarfeld, dessen Hinweis geschrieben, aber
    // nicht mit "Anmerkung uebernehmen" bestaetigt wurde: er geht mit, statt still zu
    // verschwinden. Ebenfalls nur lokal -- scheitert `senden`, bleibt das Feld offen.
    const offenerHinweisText = hinweisText.trim();
    const anmerkungenZumAbsenden: Anmerkung[] =
      offeneMarkierung && offenerHinweisText !== ''
        ? [...anmerkungen, neueAnmerkung(offeneMarkierung.anker, offenerHinweisText)]
        : anmerkungen;

    // Abgeleitet statt gewaehlt (Spec B, Abschnitt 6): Ein offener, geschriebener Hinweis macht
    // `korrigieren` bereits wahr -- eine "Freigabe mit offenem Hinweis" gibt es nicht mehr.
    const korrigieren = ableitung.textAktion === 'optimize';
    // Was die Nutzerin auf dem Hauptknopf liest. "Zur Überarbeitung senden" gilt auch, wenn nur
    // ein Bild ueberarbeitet werden soll -- dann bleibt `korrigieren` (die Textentscheidung)
    // falsch (H3, Codex-Runde 2).
    const zurUeberarbeitung = ableitung.knopf === 'Zur Überarbeitung senden';
    function offeneMarkierungBenennen(text: string) {
      setAnmerkungsFehler({ text, ort: 'feld' });
      // Das Feld kann weit ueber dem Hauptknopf stehen -- der Fokus holt es in den Blick.
      hinweisFeldRef.current?.focus();
    }
    if (offeneMarkierung) {
      const zitat = kurzesZitat(offeneMarkierung.anker.zitat);
      // M-a (Codex-Fix B2), H3 (Codex-Runde 2): Blockiert wird, wenn der Hauptknopf "Zur
      // Überarbeitung senden" heisst -- aus welchem Grund auch immer (weitere Anmerkung,
      // Gesamt-Feedback oder nur ein Bild). Die leere Markierung ginge sonst unbenannt verloren,
      // obwohl die Nutzerin eine Ueberarbeitung abschickt und sie fuer mitgeschickt halten
      // koennte. Frueher hing das an `korrigieren` (nur die Textentscheidung), sodass ein
      // Bild allein sie still fallen liess. Heisst der Knopf "Freigeben", geht die leere
      // Markierung bewusst still verloren: Sie hat nie einen Hinweis bekommen. Test "laesst eine
      // Freigabe trotz offener Markierung ohne Hinweis durch" haelt das fest.
      if (zurUeberarbeitung && offenerHinweisText === '') {
        offeneMarkierungBenennen(`Die markierte Stelle „${zitat}“ hat noch keinen Hinweis.`);
        return;
      }
    }

    // Gegen den Text, der tatsaechlich hinausgeht -- auch eine noch offene Bearbeitung kann
    // eine Anmerkung entwerten. Hier zaehlt der Inhalt, nicht der Stempel: Der Text zum Absenden
    // hat mit einer offenen Bearbeitung noch keinen eigenen Stand.
    if (korrigieren) {
      const zerlegungZumAbsenden = zerlegeInBloecke(zusammen(segmenteZumAbsenden));
      const texte = abschnittsTexte(
        zerlegungZumAbsenden,
        segmentGrenzen(segmenteZumAbsenden),
        abschnittIds,
      );
      // I-2: die offene Markierung zuerst und am Feld -- in der Liste stuende sie ohne Eintrag.
      if (
        offeneMarkierung &&
        offenerHinweisText !== '' &&
        !ankerGilt(offeneMarkierung.anker, texte)
      ) {
        markierungVeraltetMelden();
        return;
      }
      if (ungueltigeAnmerkungen(anmerkungen, texte).length > 0) {
        setAnmerkungsFehler({ text: ANMERKUNG_UNGUELTIG_TEXT, ort: 'liste' });
        return;
      }
    }

    const bildOhneFeedback = bildEntscheidungen.find(
      (b) => b.aktion === 'optimize' && b.feedback.trim() === '',
    );
    if (bildOhneFeedback) {
      setBildValidierung(BILD_VALIDIERUNG);
      return;
    }
    setBildValidierung(null);

    // Spec 2026-10-12 §5.5: n8n entfernt offene Pruefhinweise vor 7 still. Wer freigibt, bestaetigt
    // das; beim Ueberarbeiten bleiben sie fuer die naechste Runde stehen. Gezaehlt wird der Text,
    // der tatsaechlich hinausgeht. Hier, nach den Validierungen: Ein "Zurück" laesst nichts zurueck,
    // kein abgebrochener Autosave, keine Absendung.
    const offeneHinweise = zaehlePruefhinweise(zusammen(segmenteZumAbsenden));
    if (!zurUeberarbeitung && offeneHinweise > 0 && !pruefhinweiseBestaetigt) {
      setPruefhinweisFrage(offeneHinweise);
      return;
    }

    // Ruling 24 (Task-13-Re-Review NEU-10, Fix-Runde 4): Ein noch laufender Autosave-Timer
    // wuerde NACH dem Versand feuern -- die Entscheidungsroute hat den Entwurf zu diesem
    // Zeitpunkt aber schon geloescht, der verspaetete `PUT` legte ihn also wieder an und
    // der Entwurf der ALTEN Runde gewaenne beim naechsten Oeffnen gegen den frischen Text
    // aus n8n. Erst HIER abbrechen, nach den Validierungen: bricht `absenden` vorher ab,
    // tippt die Nutzerin weiter und der Autosave soll erhalten bleiben. Zweite Ebene
    // derselben Regel im Backend (`speichereEntwurf` prueft `entschiedeneRunde`) -- der
    // Timer allein deckt einen zweiten offenen Tab nicht ab.
    //
    // Punkt B des Auftrags: "Absenden" traegt den live angezeigten Text (`segmenteZumAbsenden`
    // unten) ohnehin mit sich, unabhaengig vom Entwurfspfad -- ein noch nicht gestarteter
    // Autosave wird deshalb verworfen, nicht abgewartet oder eingereiht. Fruehere Ruling-26-
    // Logik (Neuplanen im Fehlerfall) ist damit hinfaellig: Punkt C sichert getippten Text
    // jetzt allgemein beim Verlassen des Feldes/Tab-Wechsel, unabhaengig davon, ob `senden`
    // vorher gescheitert ist oder gar nicht erst versucht wurde.
    if (entwurfTimer.current) {
      clearTimeout(entwurfTimer.current);
      entwurfTimer.current = null;
    }
    const verworfenerAuftrag = verwirfAusstehenden();

    // `urSegmente` ist immer die Fassung aus n8n, nie ein geladener Entwurf (Ruling 13) --
    // ein blosses Laden eines Entwurfs zaehlt beim Absenden noch nicht als Aenderung, erst
    // eine tatsaechliche Abweichung von n8n tut es. Byte-Vergleich statt eines indexweisen
    // Segmentvergleichs: Der bleibt auch dann richtig, wenn `segmente` (unten) eine ANDERE
    // Laenge als `urSegmente` hat -- ein Vergleich Index gegen Index wuerde dort ueber
    // verschobene Indizes stolpern.
    const textGeaendert = zusammen(urSegmente) !== zusammen(segmenteZumAbsenden);
    const struktur = textAntwort.struktur;
    // Ruling 20 (Task-13-Review, HOCH-1), praezisiert in Task-13-Re-Review NEU-1:
    // `struktur.abschnitte[i]` bezeichnet ueber `herkunft[j]` genau die `urSegmente`-
    // Indizes, die in `segmente[j]` aufgegangen sind -- normalerweise nur `i` selbst, mehr
    // als einer nur dort, wo `schneideAnUrsprung` beim Laden eines Entwurfs einen Anker
    // nicht wiedergefunden hat (Ueberschrift umbenannt oder entfernt). Ein Vergleich des
    // verschmolzenen Segments gegen den ebenso zusammengesetzten Original-Abschnitt bleibt
    // dadurch auch in diesem Fall noch aussagekraeftig: Weicht er ab, gelten ALLE darin
    // verschmolzenen Abschnitte als bearbeitet (Ruling 7: "ab der Schnittstelle gilt alles
    // Folgende als geaendert") -- aber NUR diese, nicht der gesamte Ratgeber.
    // Dieselbe Vergleichslogik wie die Zusammenfassung in der Rueckmeldespalte
    // (`geaenderteSegmente`, R13-3/R14-9), in derselben Reihenfolge wie die fruehere Schleife:
    // je Segment die IDs aller darin aufgegangenen Abschnitte.
    const bearbeiteteAbschnitte =
      !textGeaendert || !struktur
        ? []
        : geaenderteSegmente(urSegmente, segmenteZumAbsenden, herkunft)
            .map((aenderung) => struktur.abschnitte[aenderung.index]?.abschnittId)
            .filter((id): id is string => typeof id === 'string');

    const art = zurUeberarbeitung ? 'ueberarbeitung' : 'freigabe';
    // BEIM KLICK, vor dem `await` (Spec 2026-09-29-absende-rennen Abschnitt 5.1): Ab hier ist die Flaeche
    // gesperrt, egal ob 202 oder die Firestore-Aufnahme zuerst ankommt.
    const meine: Absendung = { runId, runde, art };
    setFehlgeschlagen(null);
    setzeAbsendung(meine);
    // Ob das Ergebnis noch zaehlt, entscheidet die IDENTITAET dieser Absendung, nicht `laufToken`:
    // Den zaehlt der Ladeeffekt schon bei N -> null hoch, und genau das verwarf vorher den eigenen
    // Erfolg (Beleg 2026-09-29). Die Ref verliert `meine` dagegen nur, wenn der Ladeeffekt eine
    // neue Runde oeffnet oder der Lauf wechselt -- dann gehoert das Ergebnis zu einem verlassenen
    // Stand und darf die neue Runde nicht mehr anfassen.
    const absendeRunId = runId;
    setSendeFehler(null);
    try {
      await senden(runId, {
        runId,
        runde,
        // Kettenweites Feld, TEXT-Entscheidung. `EntscheidungsPayload.bilder` in
        // packages/shared regelt die Praezedenz `bilder[0]` > `aktion` ausdruecklich nur
        // fuer einen Adapter, der NUR EIN Bild kennt (die Bilder-Kette ohne Text-Zweig) --
        // fuer die Ratgeber-Kette mit Text UND Bildern sagt der Vertrag nichts. Dieses Feld
        // spiegelt hier trotzdem nur die Text-Entscheidung, `bilder[].aktion` je Bild
        // unabhaengig davon; die Rundenzaehlung in `apps/backend/src/runs-route.ts`
        // (Ruling 17, Task-12-Review) zaehlt deshalb explizit auch dann hoch, wenn NUR ein
        // Bild optimiert werden soll -- verlaesst sich also NICHT darauf, dass `aktion`
        // allein das steuert (Codex-Gate-Befund HOCH, dritte Runde; vom Review als
        // Fehlbefund-Einstufung zurueckgewiesen, siehe task-12-review.md HOCH-2).
        aktion: korrigieren ? 'optimize' : 'approve',
        gesamtFeedback: korrigieren ? textFeedback : '',
        // Eine Freigabe braucht keine Ueberarbeitungshinweise.
        anmerkungen: korrigieren ? anmerkungenZumAbsenden : [],
        bilder: bildEntscheidungen,
        ...(textGeaendert
          ? { ratgeberText: zusammen(segmenteZumAbsenden), bearbeiteteAbschnitte }
          : {}),
      });
      if (
        !aktiv.current ||
        aktuelleRunId.current !== absendeRunId ||
        absendungRef.current !== meine
      ) {
        return;
      }
      // Ruling 24 (Task-13-Re-Review NEU-13): ERST hier, im Erfolgszweig nach dem
      // Laufvergleich -- scheitert `senden`, muss die Bearbeitung unveraendert
      // offen stehen bleiben (Ruling 23). Nach Erfolg ist sie abgesendet: das Feld gehoert
      // zu, und `segmente` muss die abgesendete Fassung uebernehmen, sonst zeigte der
      // geschlossene Block wieder den Stand VOR der Bearbeitung. `herkunft` bleibt gueltig,
      // weil `ersetzeBlock` die Segmentanzahl nicht aendert.
      setzeSegmente(segmenteZumAbsenden);
      setOffenerBlock(null);
      anmerkungenZuruecksetzen();
      setAuswahl(null);
      setHinweis(null);
      // Der Klick auf den Hauptknopf nimmt einem offenen Block zuerst den Fokus: Sein Blur
      // startet ein Sichern, das neben `senden` laeuft (Task 15, Fix-Runde 1, Review I1). Die
      // Entscheidung schliesst die Runde ab -- die Route loescht den Entwurf, ein spaeter
      // ankommendes Sichern weist der Server ab (Ruling 25). Dessen Ergebnis gehoert damit zu
      // einem verlassenen Stand: Ein neuer `laufToken` laesst `useEntwurfSchreiber` es
      // uebergehen, statt "Zwischenstand vom ..." oder einen Speicherfehler wieder anzuzeigen.
      // `absendung` bleibt gesetzt: Sie haelt die Sperre, bis Firestore die naechste Runde
      // vorlegt (dann leert sie der Ladeeffekt).
      setEntwurfHinweis(null);
      setEntwurfFehler(false);
      laufToken.current += 1;
      // Nach einer Freigabe gibt es hier nichts mehr zu tun (Spec Abschnitt 3): weiter zur Statusseite.
      if (art === 'freigabe')
        navigate(`/auftraege/${runId}`, {
          replace: true,
          state: { bestaetigung: 'Freigegeben. Der Auftrag wird jetzt fertiggestellt.' },
        });
    } catch (fehler) {
      // Spec Abschnitt 5.1: Scheitert das Absenden, gibt die eigene Absendung ihre Sperre auf.
      // Bei `runde_veraltet` sperrt danach der Merker `veraltet` (unten), bis Firestore ueber
      // `offen` die andere Entscheidung zeigt. Die Eingaben bleiben erhalten (Ruling 23). ZUERST, vor jeder Wache: Sonst bliebe
      // die Sperre nach einem Laufwechsel stehen und traefe die Runde bei der Rueckkehr.
      const nochMeine = absendungRef.current === meine;
      if (nochMeine) setzeAbsendung(null);
      // Ein Fehlschlag fuer einen verlassenen Stand (anderer Lauf, neue Runde, Seite verlassen)
      // meldet dort nichts.
      if (!aktiv.current || aktuelleRunId.current !== absendeRunId || !nochMeine) return;
      // Gate-Befund MITTEL (Nacharbeit PR #38, zweite Runde): Ein vorheriges `blur` kann
      // `entwurfUngesichert` bereits auf `false` gesetzt haben (siehe `entwurfJetztSpeichern`),
      // WAEHREND sein eigener Schreibauftrag noch hinter einem laufenden Schreibvorgang im
      // Merkposten wartet -- `entwurfAusstehend.current = null` weiter oben in dieser Funktion
      // leert genau diesen wartenden Auftrag UNBEDINGT, auch wenn `senden` gleich scheitert.
      // Ohne diese Zeile bliebe die (laut Ruling 23 absichtlich offen gehaltene) Bearbeitung
      // danach ungesichert, OHNE dass ein weiteres `blur`/`visibilitychange` (Punkt C, siehe
      // `entwurfSofortSichern`) noch etwas ausloest -- derselbe Schaden, gegen den Punkt C
      // gebaut wurde, nur ueber diesen Weg erreicht. Erneut auf `true` gesetzt, greift die
      // naechste Sicherungsgelegenheit wieder -- kein eigener Sonderfall im Fehlerpfad
      // noetig, nur die Wiederherstellung des Zustands, den Punkt C ohnehin auswertet.
      entwurfUngesichert.current = true;
      // Derselbe Grund fuer die Liste: Der geleerte Auftrag hat sie getragen, auch eine leere.
      if (verworfenerAuftrag && !verworfenerAuftrag.still) anmerkungenUngesichert.current = true;
      // Bis die Freigabe-Aufnahme kommt, zeigt Firestore noch den eigenen Anspruch: Die Meldung
      // soll lesbar bleiben, statt unter einer Schicht zu verschwinden (Merker oben).
      // `runde_veraltet` nicht: Dort gehoert der Anspruch einer ANDEREN Entscheidung. Und nur,
      // solange die Runde JETZT gesperrt ist: Kam die Freigabe-Aufnahme schon vor der Antwort,
      // gibt es nichts zu erklaeren, und ein Merker blendete die Schicht einer spaeteren fremden
      // Entscheidung derselben Runde aus (Codex-Runde 1 zu PR 48).
      const istVeraltet = fehler instanceof ApiError && fehler.code === 'runde_veraltet';
      if (!istVeraltet && offenRef.current === null) {
        setFehlgeschlagen({ runId, runde });
      }
      // `runde_veraltet`, waehrend Firestore die Runde noch offen zeigt: gesperrt halten, bis die
      // Aufnahme nachzieht (Merker oben). Zeigt Firestore sie schon als entschieden, sperrt `offen`.
      if (istVeraltet && offenRef.current === runde) {
        setVeraltet({ runId, runde });
      }
      if (fehler instanceof ApiError) {
        setSendeFehler(fehler.text);
        // `runde_veraltet` heisst: die Runde ist anderswo bereits entschieden worden. Gesperrt
        // ist sie ueber `veraltet` und danach ueber Firestore (`offen`); die Meldung steht an der
        // Flaeche, bis die andere Entscheidung die Schicht bringt.
        if (fehler.code === 'runde_veraltet') {
          // "Rückgängig" haette nichts mehr zu tun (Fix-Runde 1, Minor 3, Review-Fokus 4).
          setHinweis(null);
          // R16-6: Wie im Erfolgszweig oben behandelt -- ein Klick auf den Hauptknopf blurt
          // zuerst einen offenen Block, dessen PUT (`entwurfSchreiben`) parallel zu `senden`
          // laeuft. Ohne den `laufToken`-Bump hier sieht dieser PUT beim spaeteren Scheitern
          // noch denselben Token wie beim Einreihen und setzt `entwurfFehler` -- die Spalte
          // zeigte dann faelschlich eine zusaetzliche Speicherfehler-Meldung NEBEN der
          // 409-Meldung. `runde_veraltet` ist wie ein Erfolg zu behandeln: die Runde ist
          // ohnehin schon entschieden, ein Zwischenstand dazu ist bedeutungslos.
          laufToken.current += 1;
          setEntwurfHinweis(null);
          setEntwurfFehler(false);
        }
      } else {
        setSendeFehler(ALLGEMEINER_FEHLERTEXT);
      }
      // Kein Neuplanen des Autosaves mehr hier (fruehere Ruling-26-Logik) -- Ruling 23 haelt
      // das Feld nach einem Fehlschlag ohnehin offen (siehe oben), und Punkt C sichert
      // getippten Text jetzt beim naechsten `blur`/Tab-Wechsel, unabhaengig vom Sendeergebnis.
    }
  }

  // Hinter allen Funktionsdeklarationen gesetzt: "Rückgängig" und die Entf-Taste laufen
  // spaeter und brauchen dann die Funktionen und den Sperrzustand DIESES Renders.
  const entfernenText =
    gueltigeAuswahl && bearbeitbar && !offeneMarkierung && !beruehrtOffenenBlock(gueltigeAuswahl)
      ? entfernenBeschriftung(zerlegung, grenzen, gueltigeAuswahl.bloecke) || null
      : null;
  // Eine Leiste ohne Angebot waere eine leere Werkzeugleiste, etwa nach einer 409 mit noch
  // stehender Auswahl (Fix-Runde 1, Minor 1). Ein offenes Feld steht immer: Verschwaende es, ginge
  // sein Hinweis unsichtbar mit (Fix-Runde 1, I1).
  const leisteSichtbar =
    offeneMarkierung !== null ||
    (gueltigeAuswahl !== null &&
      (kommentierenAngebot !== null || entfernenText !== null || fehlerOrt === 'leiste'));
  const leistenPosition = offeneMarkierung
    ? offeneMarkierung.position
    : (gueltigeAuswahl?.position ?? null);

  planeSpeichernRef.current = (ziel) => planeSpeichern(ziel);
  gesperrtRef.current = deaktiviert || entwurfWirdVerworfen;
  entfernenRef.current = bloeckeEntfernen;
  verankernRef.current = verankereNachSchliessen;

  return (
    <div className={stile.seite}>
      <SeitenKopf
        auftrag={auftrag}
        runId={runId}
        fassung={
          entwurfHinweis
            ? {
                bearbeitung: zwischenstandText(entwurfHinweis.gespeichertAm),
                deaktiviert: deaktiviert || entwurfWirdVerworfen,
                onContentEngine: () => setVerwerfenFrage({ runId, runde: auftrag.runde ?? 1 }),
              }
            : undefined
        }
      />

      {holeOriginal && original && (
        <OriginaltextAufklapper key={runId} quelle={original} holen={holeOriginal} />
      )}

      {ladeFehler && (
        <ErrorState text={ladeFehler} onWiederholen={() => setWiederholVersuch((n) => n + 1)} />
      )}

      {!ladeFehler && !(textAntwort && antwortAktuell) && (
        <LoadingState text="Der Text wird geladen …" />
      )}

      {!ladeFehler && textAntwort && antwortAktuell && (
        // Spec 2026-09-29-absende-rennen Abschnitt 5.3: Waehrend der Uebergabe bleibt die Flaeche sichtbar,
        // ist aber `inert` (nicht bedienbar, fuer Screenreader ausgeblendet) und ausgegraut.
        <div className={stile.schichtRahmen}>
          {/* Lupe (Spec 2026-09-30-lupe-bilder §3): Der Provider steht AUSSERHALB des Formulars.
              Sein Dialog haengt per Portal an `body`, im React-Baum neben dem Formular -- so laufen
              Ereignisse aus dem Dialog weder in die Textflaeche noch in `onSubmit`. Faellt die
              Flaeche weg (Neuladen), baut er sich samt Dialog mit ab. */}
          <BildLupeProvider>
            <form
              className={stile.arbeitsflaeche}
              data-arbeitsflaeche
              inert={zeigeSchicht}
              onSubmit={(e) => void absenden(e)}
              noValidate
            >
              <div className={stile.textspalte}>
                {/* Erstes Kind der Textspalte, kein eigenes Grid-Item (R14-6): `position: sticky`
                haftet nur innerhalb seines Containing Blocks, und die Grid-Flaeche der
                Textspalte reicht ueber den ganzen Text. */}
                <a className={stile.sprungleiste} href="#rueckmeldung">
                  Rückmeldung ({rueckmeldungsZahl}) ↓
                </a>
                {/* PDP-Kontext (Spec 2026-10-12 §5.5), nur wenn n8n ihn geliefert hat. Ausserhalb
                    von `flaechenRahmen`: kein Block, kein Abschnitt, nicht markierbar. */}
                {textAntwort.kontext && (
                  <KontextKasten
                    kontext={textAntwort.kontext}
                    pruefhinweise={offenePruefhinweise}
                    onZumHinweis={zumPruefhinweis}
                  />
                )}
                {/* Wie die Hinweiszeile ueber dem Entwurf in der Content-Factory
                    (gate/entwurf-bereich.tsx), ergaenzt um die Bilder (Rueckmeldung 2026-10-13). */}
                <p className={stile.arbeitsHinweis}>
                  <LinienSymbol name="message" groesse={14} />
                  <span>
                    Kleine Korrekturen direkt im Text, größere Änderungen mit KI überarbeiten.
                    Bilder bewerten Sie direkt am Bild mit „Passt“ oder „Ändern“.
                  </span>
                </p>
                {/* M2: Textflaeche und Leiste in einem eigenen Rahmen ohne die Sprungleiste -- die
                Leiste misst relativ zur Textflaeche und haengt absolut an diesem Rahmen.
                `tabIndex={-1}`: Fokusziel nach "Rückgängig", wenn der Block keinen Tab-Stopp
                hat (M6). */}
                <div ref={flaechenRahmen} className={stile.flaechenRahmen} tabIndex={-1}>
                  <TextFlaeche
                    zerlegung={zerlegung}
                    grenzen={grenzen}
                    abschnittIds={abschnittIds}
                    heroBild={headerBild ? bewertung(headerBild, 'hero') : undefined}
                    heroTitel={auftrag.thema}
                    galerie={
                      galerieBilder.length > 0
                        ? galerieBilder.map((b) => bewertung(b, 'galerie'))
                        : undefined
                    }
                    anmerkungen={gueltigeAnmerkungen}
                    offenerBlock={offenerBlock}
                    bearbeitbar={bearbeitbar}
                    markierbar={markierbar}
                    textStand={textStand}
                    offeneMarkierung={offeneMarkierung?.anker ?? null}
                    onOeffnen={blockOeffnen}
                    onEingabe={blockEingabe}
                    onSchliessen={blockSchliessen}
                    onAbbrechen={blockAbbrechen}
                    onAuswahl={auswahlGemeldet}
                    onAnmerkungGetroffen={anmerkungGetroffen}
                    // Codex-Review PR #112 (HOCH): Sichtbarkeit und Handler pruefen dieselbe
                    // Freigabe. Ohne Handler zeigt die Leiste am Block keine Sprechblase.
                    {...(blockKommentierbar ? { onBlockKommentieren: blockKommentieren } : {})}
                    onBlockEntfernen={blockEntfernen}
                    auswahlLeisteSichtbar={leisteSichtbar}
                  />
                  {leisteSichtbar && (
                    <AuswahlLeiste
                      position={leistenPosition}
                      kommentieren={offeneMarkierung ? null : kommentierenAngebot}
                      entfernenText={entfernenText}
                      onKommentieren={kommentieren}
                      onEntfernen={bloeckeEntfernen}
                    >
                      {offeneMarkierung && (
                        <div className={stile.kommentarfeld}>
                          <p className={stile.zitat}>„{offeneMarkierung.anker.zitat}“</p>
                          <label className="eds-field__label" htmlFor="anmerkung-hinweis">
                            Hinweis zur markierten Stelle
                          </label>
                          <textarea
                            id="anmerkung-hinweis"
                            ref={hinweisFeldRef}
                            // M5: Das Feld entsteht nur durch "Kommentieren" -- dahin geht der Fokus.
                            // Ausser ein Block ist noch offen (im Browser schliesst ihn schon der Klick
                            // auf "Kommentieren"; offen bleibt er nur im Tiefenschutz-Zweig, R16-7):
                            // Der Fokus im Feld schloesse ihn per Blur, mitten in der Bearbeitung.
                            autoFocus={offenerBlock === null}
                            className={`eds-field__input ${stile.hinweisFeld}`}
                            value={hinweisText}
                            maxLength={MAX_KOMMENTAR_ZEICHEN}
                            // Wie beim Textfeld (Ruling 22): Der Reset nach dem Verwerfen
                            // loeschte einen hier noch getippten Hinweis still.
                            disabled={deaktiviert || entwurfWirdVerworfen}
                            onChange={(e) => {
                              setHinweisText(e.target.value);
                              setAnmerkungsFehler(null);
                            }}
                          />
                          {fehlerMeldung('leiste')}
                          <div className={stile.aktionen}>
                            <button
                              type="button"
                              className="eds-button eds-button--secondary eds-button--sm"
                              disabled={deaktiviert || entwurfWirdVerworfen}
                              onClick={markierungSchliessen}
                            >
                              Abbrechen
                            </button>
                            <button
                              type="button"
                              className="eds-button eds-button--primary eds-button--sm"
                              disabled={deaktiviert || entwurfWirdVerworfen}
                              onClick={anmerkungUebernehmen}
                            >
                              Anmerkung übernehmen
                            </button>
                          </div>
                        </div>
                      )}
                      {!offeneMarkierung && fehlerMeldung('leiste')}
                    </AuswahlLeiste>
                  )}
                </div>
                {textAntwort.kontext && <AnhangBereich anhang={textAntwort.kontext.anhang} />}
              </div>
              <div className={stile.spalte}>
                <RueckmeldeSpalte
                  gesamtFeedback={textFeedback}
                  onGesamtFeedback={setTextFeedback}
                  gesamtFeedbackGesperrt={rundenlimitErreicht}
                  anmerkungen={anmerkungen}
                  ungueltig={ungueltig}
                  hervorgehoben={hervorgehoben}
                  hervorhebungsAnlass={hervorhebungsAnlass}
                  anmerkungenHinweis={
                    markierbar
                      ? 'Markieren Sie eine Textstelle, um dort einen Hinweis zu hinterlassen.'
                      : 'Keine Anmerkungen.'
                  }
                  onZurStelle={zurStelle}
                  onAnmerkungLoeschen={anmerkungEntfernen}
                  bilder={bilderZurOptimierung}
                  onZumBild={zumBild}
                  onBildZurueck={(index) => bildAendern(index, { aktion: 'approve', feedback: '' })}
                  pruefhinweise={offenePruefhinweise}
                  textAenderungen={textAenderungenText(
                    textAenderungen(urSegmente, segmente, herkunft),
                  )}
                  entwurfFehler={entwurfFehler}
                  loeschenFehler={entwurfLoeschenFehler}
                  meldungen={
                    <>
                      {sendeFehler && (
                        <div className="eds-alert eds-alert--error" role="alert">
                          <p>{sendeFehler}</p>
                        </div>
                      )}
                      {bildValidierung && (
                        <p className="eds-field__hint" role="alert">
                          {bildValidierung}
                        </p>
                      )}
                      {fehlerMeldung('liste')}
                    </>
                  }
                  // Codex-Gate-Befund HOCH (Fix-Runde 6): auch waehrend "Fassung aus n8n laden"
                  // gesperrt, sonst liesse sich die Entwurfsfassung noch absenden.
                  hauptknopf={{
                    text: ableitung.knopf,
                    deaktiviert: deaktiviert || entwurfWirdVerworfen,
                  }}
                  deaktiviert={deaktiviert || entwurfWirdVerworfen}
                />
                <KiHinweis />
              </div>
            </form>
          </BildLupeProvider>
          {/* Nur solange die Seite absenden darf: Wird die Runde inzwischen anderswo entschieden,
              verschwindet die Frage mit der Sperre. */}
          {/* Wie der Pruefhinweis-Dialog nur, solange die Seite bedienbar ist: Sperrt sie sich
              (Absenden, Uebergabe), verschwindet die Rueckfrage mit (Codex-Gate PR #70). */}
          {verwerfenFrage !== null &&
            verwerfenFrage.runId === runId &&
            verwerfenFrage.runde === (auftrag.runde ?? 1) &&
            !deaktiviert &&
            !entwurfWirdVerworfen && (
              <VerwerfenDialog
                onAbbrechen={() => setVerwerfenFrage(null)}
                onBestaetigen={() => {
                  setVerwerfenFrage(null);
                  void entwurfVerwerfen();
                }}
              />
            )}
          {pruefhinweisFrage !== null && !deaktiviert && (
            <PruefhinweisDialog
              anzahl={pruefhinweisFrage}
              onZurueck={() => setPruefhinweisFrage(null)}
              onZuDenHinweisen={() => zumPruefhinweis(0)}
              onBestaetigen={() => {
                setPruefhinweisFrage(null);
                void fuehreAbsendenAus(true);
              }}
            />
          )}
          {zeigeSchicht && art !== null && (
            <UebergabeSchicht
              art={art}
              status={auftrag.status}
              runId={runId}
              eigene={eigeneAbsendung !== null}
            />
          )}
        </div>
      )}

      <HinweisBereich
        hinweis={hinweis}
        onSchliessen={() => setHinweis(null)}
        // Dieselbe Sperre wie in `ausfuehren` (`gesperrtRef`), sichtbar am Knopf: Ein Klick
        // waehrend des Sendens oder Verwerfens schriebe nichts, schloesse aber den Hinweis.
        aktionGesperrt={deaktiviert || entwurfWirdVerworfen}
      />
    </div>
  );
}
